import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { ChromePicker } from 'react-color';
import PageEditor from './PageEditor';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('news');
  const [members, setMembers] = useState([]);
  const [news, setNews] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [excelMembers, setExcelMembers] = useState([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadData, setUploadData] = useState({
    name: '',
    category: 'Members CSV',
    description: '',
    isPublic: false
  });
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [newMember, setNewMember] = useState({
    memberId: '',
    title: 'Mr',
    firstName: '',
    lastName: '',
    gender: 'Male',
    phone: '',
    email: '',
    emergencyContact: '',
    address: '',
    city: '',
    postcode: '',
    interests: '',
    activities: '',
    status: 'Active'
  });
  
  // News management state
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [editingNews, setEditingNews] = useState<any>(null);
  const [newsFormData, setNewsFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    imagePosition: 'top',
    category: 'General'
  });
  const [newsImages, setNewsImages] = useState<FileList | null>(null);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  
  // Homepage Settings state
  const [homepageSettings, setHomepageSettings] = useState<any>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState<string | null>(null);
  
  // Page Settings state
  const [pageSettings, setPageSettings] = useState<any>(null);
  const [uploadingActivitiesHero, setUploadingActivitiesHero] = useState(false);
  const [uploadingHubHero, setUploadingHubHero] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<string>('football');
  const [pageEditorSection, setPageEditorSection] = useState<'homepage' | 'activities' | 'hub'>('homepage');
  
  const navigate = useNavigate();

  // Helper function to check if token is still valid (within 1 hour)
  const isTokenValid = () => {
    const token = localStorage.getItem('adminToken');
    const tokenTime = localStorage.getItem('adminTokenTime');
    
    if (!token || !tokenTime) {
      return false;
    }
    
    const now = Date.now();
    const tokenAge = now - parseInt(tokenTime);
    const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
    
    return tokenAge < oneHour;
  };

  useEffect(() => {
    if (!isTokenValid()) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminTokenTime');
      navigate('/admin');
      return;
    }
    
    // Setup axios interceptor for handling 401 errors
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired on backend
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminTokenTime');
          navigate('/admin');
        }
        return Promise.reject(error);
      }
    );
    
    fetchData();
    
    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [navigate]);

  const fetchData = async () => {
    const token = localStorage.getItem('adminToken');
    const config = { headers: { Authorization: `Bearer ${token}` } };
    
    try {
      const [membersRes, newsRes, bookingsRes, docsRes, excelRes, homepageRes, pageRes] = await Promise.all([
        axios.get(`${API_URL}/members`, config),
        axios.get(`${API_URL}/news`, config),
        axios.get(`${API_URL}/hub/bookings`, config),
        axios.get(`${API_URL}/docs`, config),
        axios.get(`${API_URL}/members/excel/view`),
        axios.get(`${API_URL}/homepage`),
        axios.get(`${API_URL}/pages`)
      ]);
      
      setMembers(membersRes.data.data || []);
      setNews(newsRes.data.data || []);
      setBookings(bookingsRes.data.data || []);
      setDocuments(docsRes.data.data || []);
      setExcelMembers(excelRes.data.data || []);
      setHomepageSettings(homepageRes.data.data || {});
      setPageSettings(pageRes.data.data || {});
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleFileUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile) {
      alert('Please select a file');
      return;
    }

    const token = localStorage.getItem('adminToken');
    const formData = new FormData();
    formData.append('document', uploadFile);
    formData.append('name', uploadData.name || uploadFile.name);
    formData.append('category', uploadData.category);
    formData.append('description', uploadData.description);
    formData.append('isPublic', String(uploadData.isPublic));

    try {
      await axios.post(`${API_URL}/docs/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      alert('Document uploaded successfully!');
      setUploadFile(null);
      setUploadData({ name: '', category: 'Members CSV', description: '', isPublic: false });
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error uploading document');
    }
  };

  const handleDeleteDoc = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${API_URL}/docs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Document deleted successfully!');
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error deleting document');
    }
  };

  // News Management Handlers
  const handleOpenNewsModal = (newsItem?: any) => {
    if (newsItem) {
      setEditingNews(newsItem);
      setNewsFormData({
        title: newsItem.title || '',
        content: newsItem.content || '',
        excerpt: newsItem.excerpt || '',
        imagePosition: newsItem.imagePosition || 'top',
        category: newsItem.category || 'General'
      });
    } else {
      setEditingNews(null);
      setNewsFormData({
        title: '',
        content: '',
        excerpt: '',
        imagePosition: 'top',
        category: 'General'
      });
    }
    setNewsImages(null);
    setPreviewImages([]);
    setShowNewsModal(true);
  };

  const handleNewsImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setNewsImages(files);
      
      // Create preview URLs
      const previews: string[] = [];
      Array.from(files).forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          previews.push(reader.result as string);
          if (previews.length === files.length) {
            setPreviewImages(previews);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleSaveNews = async () => {
    const token = localStorage.getItem('adminToken');
    const formData = new FormData();
    
    formData.append('title', newsFormData.title);
    formData.append('content', newsFormData.content);
    formData.append('excerpt', newsFormData.excerpt);
    formData.append('imagePosition', newsFormData.imagePosition);
    formData.append('category', newsFormData.category);
    
    // Append images if any
    if (newsImages) {
      Array.from(newsImages).forEach(file => {
        formData.append('images', file);
      });
    }
    
    // If editing, keep existing images
    if (editingNews) {
      formData.append('keepExistingImages', 'true');
    }
    
    try {
      if (editingNews) {
        // Update existing news
        await axios.put(`${API_URL}/news/${editingNews._id}`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        alert('News article updated successfully!');
      } else {
        // Create new news
        await axios.post(`${API_URL}/news`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        alert('News article created successfully!');
      }
      setShowNewsModal(false);
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error saving news article');
    }
  };

  const handleDeleteNews = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this news article? This will also delete associated images.')) return;
    
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${API_URL}/news/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('News article deleted successfully!');
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error deleting news article');
    }
  };

  const handleToggleArchive = async (id: string) => {
    const token = localStorage.getItem('adminToken');
    try {
      await axios.patch(`${API_URL}/news/${id}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error toggling archive status');
    }
  };

  const handleDownloadDoc = async (id: string) => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${API_URL}/docs/download/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = response.headers['content-disposition']?.split('filename=')[1] || 'document';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error downloading document');
    }
  };

  const handleDownloadCSV = () => {
    const headers = ['Member ID', 'Title', 'First Name', 'Last Name', 'Gender', 'Phone', 'Email', 'Emergency Contact', 'Address', 'City', 'Postcode', 'Interests', 'Activities', 'Joined Date', 'Status'];
    
    const csvRows = [headers.join(',')];
    
    excelMembers.forEach((member: any) => {
      const row = [
        member['Member ID'] || '',
        member['Title'] || '',
        member['First Name'] || '',
        member['Last Name'] || '',
        member['Gender'] || '',
        member['Phone'] || '',
        member['Email'] || '',
        member['Emergency Contact'] || '',
        member['Address'] ? `"${member['Address'].replace(/"/g, '""')}"` : '',
        member['City'] || '',
        member['Postcode'] || '',
        member['Interests'] || '',
        member['Activities'] || '',
        member['Joined Date'] || '',
        member['Status'] || ''
      ];
      csvRows.push(row.join(','));
    });
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `members-database-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleDownloadXLSX = async () => {
    try {
      const XLSX = await import('xlsx');
      const worksheet = XLSX.utils.json_to_sheet(excelMembers);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Members');
      XLSX.writeFile(workbook, `members-database-${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      alert('Error generating Excel file. Please try CSV format instead.');
    }
  };

  const handleAddMember = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      // Prepare member data matching Excel structure
      const memberData = {
        'Member ID': newMember.memberId,
        'Title': newMember.title,
        'First Name': newMember.firstName,
        'Last Name': newMember.lastName,
        'Gender': newMember.gender,
        'Phone': newMember.phone,
        'Email': newMember.email,
        'Emergency Contact': newMember.emergencyContact,
        'Address': newMember.address,
        'City': newMember.city,
        'Postcode': newMember.postcode,
        'Interests': newMember.interests,
        'Activities': newMember.activities,
        'Joined Date': new Date().toLocaleDateString('en-GB'),
        'Status': newMember.status
      };

      await axios.post(`${API_URL}/members/excel/add`, memberData, config);
      alert('Member added successfully!');
      setShowAddMemberModal(false);
      setNewMember({
        memberId: '',
        title: 'Mr',
        firstName: '',
        lastName: '',
        gender: 'Male',
        phone: '',
        email: '',
        emergencyContact: '',
        address: '',
        city: '',
        postcode: '',
        interests: '',
        activities: '',
        status: 'Active'
      });
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error adding member');
    }
  };

  const handleDeleteMember = async (memberId: string) => {
    if (!window.confirm(`Are you sure you want to delete member ${memberId}? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };

      await axios.delete(`${API_URL}/members/excel/${memberId}`, config);
      alert('Member deleted successfully!');
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error deleting member');
    }
  };

  // Homepage Settings Handlers
  const handleSaveHomepageSettings = async () => {
    if (!isTokenValid()) {
      alert('Your session has expired. Please login again.');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminTokenTime');
      navigate('/admin');
      return;
    }
    
    const token = localStorage.getItem('adminToken');
    
    try {
      console.log('Saving homepage settings:', homepageSettings);
      
      // Extract only the settings fields, exclude MongoDB metadata
      const settingsToSave = {
        hero: homepageSettings.hero,
        logo: homepageSettings.logo,
        stats: homepageSettings.stats,
        theme: homepageSettings.theme,
        contact: homepageSettings.contact,
        sections: homepageSettings.sections,
        footer: homepageSettings.footer
      };
      
      console.log('Cleaned settings to save:', settingsToSave);
      
      const response = await axios.put(`${API_URL}/homepage`, settingsToSave, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('Save response:', response.data);
      alert('Homepage settings saved successfully!');
      fetchData();
    } catch (error: any) {
      console.error('Error saving homepage settings:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Error saving homepage settings';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    
    const token = localStorage.getItem('adminToken');
    const formData = new FormData();
    formData.append('logo', e.target.files[0]);
    
    setUploadingLogo(true);
    try {
      await axios.post(`${API_URL}/homepage/upload-logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      alert('Logo uploaded successfully!');
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error uploading logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleHeroImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    
    const token = localStorage.getItem('adminToken');
    const formData = new FormData();
    formData.append('heroImage', e.target.files[0]);
    
    setUploadingHero(true);
    try {
      await axios.post(`${API_URL}/homepage/upload-hero-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      alert('Hero image uploaded successfully!');
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error uploading hero image');
    } finally {
      setUploadingHero(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Are you sure you want to delete the logo?')) return;
    
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${API_URL}/homepage/logo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Logo deleted successfully!');
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error deleting logo');
    }
  };

  const handleDeleteHeroImage = async () => {
    if (!window.confirm('Are you sure you want to delete the hero image?')) return;
    
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${API_URL}/homepage/hero-image`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Hero image deleted successfully!');
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error deleting hero image');
    }
  };

  const handleResetHomepage = async () => {
    if (!window.confirm('Reset all homepage settings to defaults? This will delete uploaded images. This action cannot be undone.')) return;
    
    const token = localStorage.getItem('adminToken');
    try {
      await axios.post(`${API_URL}/homepage/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Homepage settings reset to defaults!');
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error resetting homepage settings');
    }
  };

  const handleColorChange = (colorKey: string, value: string) => {
    setHomepageSettings({
      ...homepageSettings,
      theme: {
        ...homepageSettings.theme,
        [colorKey]: value
      }
    });
  };

  // Activities Page Settings Handlers
  const handleSaveActivitiesSettings = async () => {
    if (!isTokenValid()) {
      alert('Your session has expired. Please login again.');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminTokenTime');
      navigate('/admin');
      return;
    }
    
    const token = localStorage.getItem('adminToken');
    
    try {
      console.log('Saving activities settings:', pageSettings?.activitiesPage);
      
      const settingsToSave = {
        hero: pageSettings.activitiesPage.hero,
        description: pageSettings.activitiesPage.description,
        showActivities: pageSettings.activitiesPage.showActivities
      };
      
      const response = await axios.put(`${API_URL}/pages/activities`, settingsToSave, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('Save response:', response.data);
      alert('Activities settings saved successfully!');
      fetchData();
    } catch (error: any) {
      console.error('Error saving activities settings:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error saving activities settings';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleActivitiesHeroUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    
    const token = localStorage.getItem('adminToken');
    const formData = new FormData();
    formData.append('heroImage', e.target.files[0]);
    
    setUploadingActivitiesHero(true);
    try {
      await axios.post(`${API_URL}/pages/upload-hero/activities`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      alert('Activities hero image uploaded successfully!');
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error uploading hero image');
    } finally {
      setUploadingActivitiesHero(false);
    }
  };

  const handleDeleteActivitiesHero = async () => {
    if (!window.confirm('Are you sure you want to delete the activities hero image?')) return;
    
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${API_URL}/pages/hero-image/activities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Activities hero image deleted successfully!');
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error deleting hero image');
    }
  };

  const handleResetActivities = async () => {
    if (!window.confirm('Reset activities page settings to defaults? This action cannot be undone.')) return;
    
    const token = localStorage.getItem('adminToken');
    try {
      await axios.post(`${API_URL}/pages/reset/activities`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Activities settings reset to defaults!');
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error resetting activities settings');
    }
  };

  // Community Hub Page Settings Handlers
  const handleSaveHubSettings = async () => {
    if (!isTokenValid()) {
      alert('Your session has expired. Please login again.');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminTokenTime');
      navigate('/admin');
      return;
    }
    
    const token = localStorage.getItem('adminToken');
    
    try {
      console.log('Saving hub settings:', pageSettings?.communityHubPage);
      
      const settingsToSave = {
        hero: pageSettings.communityHubPage.hero,
        description: pageSettings.communityHubPage.description,
        features: pageSettings.communityHubPage.features,
        pricing: pageSettings.communityHubPage.pricing,
        gallery: pageSettings.communityHubPage.gallery
      };
      
      const response = await axios.put(`${API_URL}/pages/community-hub`, settingsToSave, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('Save response:', response.data);
      alert('Community Hub settings saved successfully!');
      fetchData();
    } catch (error: any) {
      console.error('Error saving hub settings:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error saving hub settings';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleHubHeroUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    
    const token = localStorage.getItem('adminToken');
    const formData = new FormData();
    formData.append('heroImage', e.target.files[0]);
    
    setUploadingHubHero(true);
    try {
      await axios.post(`${API_URL}/pages/upload-hero/community-hub`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      alert('Hub hero image uploaded successfully!');
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error uploading hero image');
    } finally {
      setUploadingHubHero(false);
    }
  };

  const handleDeleteHubHero = async () => {
    if (!window.confirm('Are you sure you want to delete the hub hero image?')) return;
    
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${API_URL}/pages/hero-image/community-hub`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Hub hero image deleted successfully!');
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error deleting hero image');
    }
  };

  const handleResetHub = async () => {
    if (!window.confirm('Reset hub page settings to defaults? This action cannot be undone.')) return;
    
    const token = localStorage.getItem('adminToken');
    try {
      await axios.post(`${API_URL}/pages/reset/community-hub`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Hub settings reset to defaults!');
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error resetting hub settings');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminTokenTime');
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-primary text-white p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-2xl font-bold">LSC Admin Dashboard</h1>
          <button onClick={handleLogout} className="bg-red-500 hover:bg-red-600 px-4 py-2 rounded">
            Logout
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto p-6">
        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex space-x-8">
            {['news', 'excel-database', 'bookings', 'documents', 'page-editor'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-2 px-4 font-semibold border-b-2 transition ${
                  activeTab === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'page-editor' ? '📄 Page Editor' :
                 tab.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow p-6">
          {activeTab === 'news' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold">📰 News Management</h2>
                  <p className="text-gray-600 mt-1">
                    Total: {news.filter((n: any) => !n.isArchived).length} Active | 
                    {' '}{news.filter((n: any) => n.isArchived).length} Archived
                  </p>
                </div>
                <button
                  onClick={() => handleOpenNewsModal()}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg shadow-lg transition-colors font-semibold"
                >
                  ➕ Add News Article
                </button>
              </div>

              {/* Active News */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm mr-2">Active</span>
                  News Articles
                </h3>
                <div className="space-y-4">
                  {news.filter((item: any) => !item.isArchived).length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No active news articles. Click "Add News Article" to create one.</p>
                  ) : (
                    news.filter((item: any) => !item.isArchived).map((item: any) => (
                      <div key={item._id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow bg-white">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-bold text-xl mb-2">{item.title}</h3>
                            <p className="text-sm text-gray-600 mb-3">
                              📅 Published: {new Date(item.publishedDate).toLocaleDateString('en-GB', { 
                                day: '2-digit', 
                                month: 'long', 
                                year: 'numeric' 
                              })}
                              {item.category && <span className="ml-4 bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">{item.category}</span>}
                            </p>
                            {item.excerpt && <p className="text-gray-700 mb-3">{item.excerpt}</p>}
                            {item.images && item.images.length > 0 && (
                              <p className="text-sm text-gray-500">🖼️ {item.images.length} image(s) attached</p>
                            )}
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => handleOpenNewsModal(item)}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors text-sm"
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => handleToggleArchive(item._id)}
                              className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded transition-colors text-sm"
                            >
                              📦 Archive
                            </button>
                            <button
                              onClick={() => handleDeleteNews(item._id)}
                              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded transition-colors text-sm"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Archived News */}
              {news.filter((item: any) => item.isArchived).length > 0 && (
                <div>
                  <h3 className="text-xl font-semibold mb-4 flex items-center">
                    <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm mr-2">Archived</span>
                    News Articles
                  </h3>
                  <div className="space-y-4">
                    {news.filter((item: any) => item.isArchived).map((item: any) => (
                      <div key={item._id} className="border border-gray-300 rounded-lg p-6 bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 opacity-75">
                            <h3 className="font-bold text-xl mb-2">{item.title}</h3>
                            <p className="text-sm text-gray-600 mb-3">
                              📅 Published: {new Date(item.publishedDate).toLocaleDateString('en-GB', { 
                                day: '2-digit', 
                                month: 'long', 
                                year: 'numeric' 
                              })}
                            </p>
                            {item.excerpt && <p className="text-gray-700">{item.excerpt}</p>}
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => handleToggleArchive(item._id)}
                              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors text-sm"
                            >
                              ↩️ Unarchive
                            </button>
                            <button
                              onClick={() => handleDeleteNews(item._id)}
                              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded transition-colors text-sm"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add/Edit News Modal */}
              {showNewsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="sticky top-0 bg-white border-b p-6 z-10">
                      <h3 className="text-2xl font-bold">
                        {editingNews ? '✏️ Edit News Article' : '➕ Add News Article'}
                      </h3>
                    </div>
                    
                    <div className="p-6 space-y-6">
                      {/* Title */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Title *
                        </label>
                        <input
                          type="text"
                          value={newsFormData.title}
                          onChange={(e) => setNewsFormData({...newsFormData, title: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder="Enter article title"
                          required
                        />
                      </div>

                      {/* Category */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Category
                          </label>
                          <select
                            value={newsFormData.category}
                            onChange={(e) => setNewsFormData({...newsFormData, category: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                          >
                            <option value="General">General</option>
                            <option value="Events">Events</option>
                            <option value="Sports">Sports</option>
                            <option value="Community">Community</option>
                            <option value="Announcement">Announcement</option>
                          </select>
                        </div>

                        {/* Image Position */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Image Position
                          </label>
                          <select
                            value={newsFormData.imagePosition}
                            onChange={(e) => setNewsFormData({...newsFormData, imagePosition: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                          >
                            <option value="top">Top of Article</option>
                            <option value="bottom">Bottom of Article</option>
                          </select>
                        </div>
                      </div>

                      {/* Excerpt */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Excerpt (Short Summary)
                        </label>
                        <textarea
                          value={newsFormData.excerpt}
                          onChange={(e) => setNewsFormData({...newsFormData, excerpt: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                          rows={2}
                          placeholder="Brief summary for preview (optional)"
                        />
                      </div>

                      {/* Main Content - WYSIWYG Editor */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Main Article Content *
                        </label>
                        <ReactQuill
                          theme="snow"
                          value={newsFormData.content}
                          onChange={(value) => setNewsFormData({...newsFormData, content: value})}
                          modules={{
                            toolbar: [
                              [{ 'header': [1, 2, 3, false] }],
                              ['bold', 'italic', 'underline', 'strike'],
                              [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                              [{ 'align': [] }],
                              ['link'],
                              ['clean']
                            ]
                          }}
                          className="bg-white"
                          style={{ height: '200px', marginBottom: '50px' }}
                        />
                      </div>

                      {/* Image Upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Upload Images (Max 10 images, 5MB each)
                        </label>
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          multiple
                          onChange={handleNewsImageChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                        {previewImages.length > 0 && (
                          <div className="mt-4 grid grid-cols-3 gap-4">
                            {previewImages.map((preview, index) => (
                              <img 
                                key={index} 
                                src={preview} 
                                alt={`Preview ${index + 1}`} 
                                className="w-full h-32 object-cover rounded border"
                              />
                            ))}
                          </div>
                        )}
                        {editingNews && editingNews.images && editingNews.images.length > 0 && (
                          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                            <p className="text-sm text-blue-800">
                              📎 Current article has {editingNews.images.length} image(s). 
                              New uploads will be added to existing images.
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Publish Date Info */}
                      <div className="p-4 bg-green-50 border border-green-200 rounded">
                        <p className="text-sm text-green-800">
                          📅 <strong>Published Date:</strong> {editingNews ? 
                            new Date(editingNews.publishedDate).toLocaleDateString('en-GB', { 
                              day: '2-digit', 
                              month: 'long', 
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 
                            'Will be set to current date/time automatically'}
                        </p>
                      </div>
                    </div>

                    {/* Modal Footer */}
                    <div className="sticky bottom-0 bg-gray-50 border-t p-6 flex justify-end gap-3">
                      <button
                        onClick={() => setShowNewsModal(false)}
                        className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveNews}
                        className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-semibold"
                      >
                        {editingNews ? '💾 Update Article' : '✅ Publish Article'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'excel-database' && (
            <div>
              <h2 className="text-2xl font-bold mb-4">Excel Database - Participants Database</h2>
              <p className="text-gray-600 mb-4">
                Total Members in Excel: {excelMembers.length}
                <span className="ml-4 text-sm text-blue-600">
                  📄 File: Participants Database-251208.xlsx
                </span>
              </p>
              
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800">
                  <strong>ℹ️ Note:</strong> This view shows the actual Excel file data. 
                  New member registrations are automatically added to this file and members receive their ID via email.
                </p>
              </div>

              <div className="mb-4">
                <button 
                  onClick={() => setShowAddMemberModal(true)}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg shadow transition-colors"
                >
                  ➕ Add New Member
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">First Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gender</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Emergency Contact</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Address</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">City</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Postcode</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Interests</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Activities</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {[...excelMembers].reverse().map((member: any, index: number) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600">
                          {member['Member ID']}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">{member['Title']}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">{member['First Name']}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">{member['Last Name']}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">{member['Gender']}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">{member['Phone']}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">{member['Email']}</td>
                        <td className="px-4 py-3 text-sm">{member['Emergency Contact']}</td>
                        <td className="px-4 py-3 text-sm max-w-xs truncate">{member['Address']}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">{member['City']}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">{member['Postcode']}</td>
                        <td className="px-4 py-3 text-sm max-w-xs">
                          <div className="truncate" title={member['Interests']}>
                            {member['Interests']}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm max-w-xs">
                          <div className="truncate" title={member['Activities']}>
                            {member['Activities']}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">{member['Joined Date']}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            {member['Status']}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <button
                            onClick={() => handleDeleteMember(member['Member ID'])}
                            className="text-red-600 hover:text-red-800 font-medium transition-colors"
                            title="Delete Member"
                          >
                            🗑️ Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {excelMembers.length > 0 && (
                <div className="mt-6 flex gap-4 items-center">
                  <button 
                    onClick={handleDownloadCSV}
                    className="bg-primary text-white px-6 py-2 rounded hover:bg-blue-700 transition flex items-center gap-2"
                  >
                    <span>📥</span>
                    <span>Download CSV</span>
                  </button>
                  <button 
                    onClick={handleDownloadXLSX}
                    className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 transition flex items-center gap-2"
                  >
                    <span>📥</span>
                    <span>Download Excel (XLSX)</span>
                  </button>
                  <span className="text-gray-600 text-sm ml-2">
                    Total Members: {excelMembers.length}
                  </span>
                </div>
              )}

              {excelMembers.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <p>No members found in Excel file.</p>
                  <p className="text-sm mt-2">Members will appear here after registration.</p>
                </div>
              )}

              {/* Add Member Modal */}
              {showAddMemberModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="text-2xl font-bold text-gray-900">Add New Member</h3>
                        <button
                          onClick={() => setShowAddMemberModal(false)}
                          className="text-gray-500 hover:text-gray-700 text-2xl"
                        >
                          ✕
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Member ID *</label>
                          <input
                            type="text"
                            value={newMember.memberId}
                            onChange={(e) => setNewMember({...newMember, memberId: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., LSC0331"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                          <select
                            value={newMember.title}
                            onChange={(e) => setNewMember({...newMember, title: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          >
                            <option value="Mr">Mr</option>
                            <option value="Mrs">Mrs</option>
                            <option value="Miss">Miss</option>
                            <option value="Ms">Ms</option>
                            <option value="Dr">Dr</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                          <input
                            type="text"
                            value={newMember.firstName}
                            onChange={(e) => setNewMember({...newMember, firstName: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                          <input
                            type="text"
                            value={newMember.lastName}
                            onChange={(e) => setNewMember({...newMember, lastName: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Gender *</label>
                          <select
                            value={newMember.gender}
                            onChange={(e) => setNewMember({...newMember, gender: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          >
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                            <option value="Prefer not to say">Prefer not to say</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                          <input
                            type="tel"
                            value={newMember.phone}
                            onChange={(e) => setNewMember({...newMember, phone: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                          <input
                            type="email"
                            value={newMember.email}
                            onChange={(e) => setNewMember({...newMember, email: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Emergency Contact</label>
                          <input
                            type="text"
                            value={newMember.emergencyContact}
                            onChange={(e) => setNewMember({...newMember, emergencyContact: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Name and phone number (optional)"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Address *</label>
                          <textarea
                            value={newMember.address}
                            onChange={(e) => setNewMember({...newMember, address: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={2}
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                          <input
                            type="text"
                            value={newMember.city}
                            onChange={(e) => setNewMember({...newMember, city: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Postcode *</label>
                          <input
                            type="text"
                            value={newMember.postcode}
                            onChange={(e) => setNewMember({...newMember, postcode: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Interests</label>
                          <input
                            type="text"
                            value={newMember.interests}
                            onChange={(e) => setNewMember({...newMember, interests: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., Reading, Gardening, Cooking"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Activities</label>
                          <input
                            type="text"
                            value={newMember.activities}
                            onChange={(e) => setNewMember({...newMember, activities: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., Football, Walking, Aerobics"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                          <select
                            value={newMember.status}
                            onChange={(e) => setNewMember({...newMember, status: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                            <option value="Pending">Pending</option>
                          </select>
                        </div>
                      </div>

                      <div className="mt-6 flex justify-end gap-4">
                        <button
                          onClick={() => setShowAddMemberModal(false)}
                          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleAddMember}
                          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                        >
                          Add Member
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'bookings' && (
            <BookingsManagement 
              bookings={bookings}
              onRefresh={fetchData}
            />
          )}

          {activeTab === 'documents' && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Documents Management</h2>
              
              {/* Upload Form */}
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <h3 className="text-xl font-semibold mb-4">📤 Upload New Document</h3>
                <form onSubmit={handleFileUpload} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select File (CSV, Excel, PDF, Word, Text) *
                    </label>
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls,.pdf,.doc,.docx,.txt"
                      onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Document Name
                      </label>
                      <input
                        type="text"
                        value={uploadData.name}
                        onChange={(e) => setUploadData({ ...uploadData, name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="e.g., Members List 2024"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category *
                      </label>
                      <select
                        value={uploadData.category}
                        onChange={(e) => setUploadData({ ...uploadData, category: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        required
                      >
                        <option value="Members CSV">Members CSV</option>
                        <option value="Hub Agreement">Hub Agreement</option>
                        <option value="Policy">Policy</option>
                        <option value="Report">Report</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={uploadData.description}
                      onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      rows={3}
                      placeholder="Brief description of the document"
                    />
                  </div>

                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={uploadData.isPublic}
                        onChange={(e) => setUploadData({ ...uploadData, isPublic: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Make this document publicly accessible
                      </span>
                    </label>
                  </div>

                  <button type="submit" className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-dark transition">
                    Upload Document
                  </button>
                </form>
              </div>

              {/* Documents List */}
              <div>
                <h3 className="text-xl font-semibold mb-4">📁 All Documents ({documents.length})</h3>
                <div className="space-y-4">
                  {documents.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No documents uploaded yet. Upload your first document above!</p>
                    </div>
                  ) : (
                    documents.map((doc: any) => (
                      <div key={doc._id} className="border border-gray-200 p-4 rounded-lg hover:shadow-md transition">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-bold text-lg">{doc.name}</h4>
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                {doc.category}
                              </span>
                              {doc.isPublic && (
                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                  Public
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{doc.description || 'No description'}</p>
                            <div className="flex gap-4 text-xs text-gray-500">
                              <span>📄 {doc.originalName}</span>
                              <span>📦 {(doc.fileSize / 1024).toFixed(2)} KB</span>
                              <span>👤 {doc.uploadedBy}</span>
                              <span>📅 {new Date(doc.createdAt).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => handleDownloadDoc(doc._id)}
                              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm transition"
                            >
                              Download
                            </button>
                            <button
                              onClick={() => handleDeleteDoc(doc._id)}
                              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded text-sm transition"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Homepage Settings Tab */}
          {activeTab === 'homepage-settings' && !homepageSettings && (
            <div className="text-center py-12">
              <p className="text-gray-600">Loading homepage settings...</p>
              <p className="text-sm text-gray-500 mt-2">If this message persists, please refresh the page.</p>
            </div>
          )}

          {activeTab === 'homepage-settings' && homepageSettings && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">🏠 Homepage Settings</h2>
                <div className="flex gap-2">
                  <button
                    onClick={handleResetHomepage}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition"
                  >
                    Reset to Defaults
                  </button>
                  <button
                    onClick={handleSaveHomepageSettings}
                    className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded transition"
                  >
                    Save All Changes
                  </button>
                </div>
              </div>

              {/* Hero Section */}
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-semibold mb-4">🎯 Hero Section</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                    <input
                      type="text"
                      value={homepageSettings.hero?.title || ''}
                      onChange={(e) => setHomepageSettings({
                        ...homepageSettings,
                        hero: { ...homepageSettings.hero, title: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                    <textarea
                      value={homepageSettings.hero?.subtitle || ''}
                      onChange={(e) => setHomepageSettings({
                        ...homepageSettings,
                        hero: { ...homepageSettings.hero, subtitle: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Primary Button Text</label>
                      <input
                        type="text"
                        value={homepageSettings.hero?.buttonText || ''}
                        onChange={(e) => setHomepageSettings({
                          ...homepageSettings,
                          hero: { ...homepageSettings.hero, buttonText: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Button Text</label>
                      <input
                        type="text"
                        value={homepageSettings.hero?.secondaryButtonText || ''}
                        onChange={(e) => setHomepageSettings({
                          ...homepageSettings,
                          hero: { ...homepageSettings.hero, secondaryButtonText: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hero Background Image</label>
                    {homepageSettings.hero?.backgroundImage && (
                      <div className="mb-2">
                        <img
                          src={`http://localhost:5000${homepageSettings.hero.backgroundImage}`}
                          alt="Hero Background"
                          className="h-32 w-full object-cover rounded-lg"
                        />
                        <button
                          onClick={handleDeleteHeroImage}
                          className="mt-2 bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm"
                        >
                          Delete Hero Image
                        </button>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleHeroImageUpload}
                      disabled={uploadingHero}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    {uploadingHero && <p className="text-sm text-gray-500 mt-1">Uploading...</p>}
                  </div>
                </div>
              </div>

              {/* Logo Section */}
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-semibold mb-4">📷 Logo</h3>
                {homepageSettings.logo && (
                  <div className="mb-4">
                    <img
                      src={`http://localhost:5000${homepageSettings.logo}`}
                      alt="Logo"
                      className="h-24 object-contain bg-white p-2 rounded-lg"
                    />
                    <button
                      onClick={handleDeleteLogo}
                      className="mt-2 bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm"
                    >
                      Delete Logo
                    </button>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  disabled={uploadingLogo}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
                {uploadingLogo && <p className="text-sm text-gray-500 mt-1">Uploading...</p>}
              </div>

              {/* Stats Section */}
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-semibold mb-4">📊 Statistics</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Members</label>
                    <input
                      type="text"
                      value={homepageSettings.stats?.members || ''}
                      onChange={(e) => setHomepageSettings({
                        ...homepageSettings,
                        stats: { ...homepageSettings.stats, members: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Activities</label>
                    <input
                      type="text"
                      value={homepageSettings.stats?.activities || ''}
                      onChange={(e) => setHomepageSettings({
                        ...homepageSettings,
                        stats: { ...homepageSettings.stats, activities: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Year Established</label>
                    <input
                      type="text"
                      value={homepageSettings.stats?.yearEstablished || ''}
                      onChange={(e) => setHomepageSettings({
                        ...homepageSettings,
                        stats: { ...homepageSettings.stats, yearEstablished: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hub Capacity</label>
                    <input
                      type="text"
                      value={homepageSettings.stats?.hubCapacity || ''}
                      onChange={(e) => setHomepageSettings({
                        ...homepageSettings,
                        stats: { ...homepageSettings.stats, hubCapacity: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
              </div>

              {/* Theme Colors */}
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-semibold mb-4">🎨 Theme Colors</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  {[
                    { key: 'primaryColor', label: 'Primary Color' },
                    { key: 'secondaryColor', label: 'Secondary Color' },
                    { key: 'accentColor', label: 'Accent Color' },
                    { key: 'backgroundColor', label: 'Background Color' },
                    { key: 'textColor', label: 'Text Color' }
                  ].map(({ key, label }) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-12 h-12 rounded border border-gray-300 cursor-pointer"
                          style={{ backgroundColor: homepageSettings.theme?.[key] || '#ffffff' }}
                          onClick={() => setShowColorPicker(showColorPicker === key ? null : key)}
                        />
                        <input
                          type="text"
                          value={homepageSettings.theme?.[key] || ''}
                          onChange={(e) => handleColorChange(key, e.target.value)}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="#000000"
                        />
                      </div>
                      {showColorPicker === key && (
                        <div className="absolute z-10 mt-2">
                          <div
                            className="fixed inset-0"
                            onClick={() => setShowColorPicker(null)}
                          />
                          <ChromePicker
                            color={homepageSettings.theme?.[key] || '#ffffff'}
                            onChange={(color) => handleColorChange(key, color.hex)}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Font Family</label>
                  <select
                    value={homepageSettings.theme?.fontFamily || ''}
                    onChange={(e) => setHomepageSettings({
                      ...homepageSettings,
                      theme: { ...homepageSettings.theme, fontFamily: e.target.value }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="Inter, system-ui, sans-serif">Inter (Default)</option>
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="Helvetica, sans-serif">Helvetica</option>
                    <option value="Georgia, serif">Georgia</option>
                    <option value="'Times New Roman', serif">Times New Roman</option>
                    <option value="'Courier New', monospace">Courier New</option>
                    <option value="Verdana, sans-serif">Verdana</option>
                    <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                  </select>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-semibold mb-4">📞 Contact Information</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                      <input
                        type="text"
                        value={homepageSettings.contact?.phone || ''}
                        onChange={(e) => setHomepageSettings({
                          ...homepageSettings,
                          contact: { ...homepageSettings.contact, phone: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={homepageSettings.contact?.email || ''}
                        onChange={(e) => setHomepageSettings({
                          ...homepageSettings,
                          contact: { ...homepageSettings.contact, email: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                    <input
                      type="text"
                      value={homepageSettings.contact?.address || ''}
                      onChange={(e) => setHomepageSettings({
                        ...homepageSettings,
                        contact: { ...homepageSettings.contact, address: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Facebook URL</label>
                      <input
                        type="text"
                        value={homepageSettings.contact?.socialMedia?.facebook || ''}
                        onChange={(e) => setHomepageSettings({
                          ...homepageSettings,
                          contact: {
                            ...homepageSettings.contact,
                            socialMedia: { ...homepageSettings.contact?.socialMedia, facebook: e.target.value }
                          }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Twitter URL</label>
                      <input
                        type="text"
                        value={homepageSettings.contact?.socialMedia?.twitter || ''}
                        onChange={(e) => setHomepageSettings({
                          ...homepageSettings,
                          contact: {
                            ...homepageSettings.contact,
                            socialMedia: { ...homepageSettings.contact?.socialMedia, twitter: e.target.value }
                          }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Instagram URL</label>
                      <input
                        type="text"
                        value={homepageSettings.contact?.socialMedia?.instagram || ''}
                        onChange={(e) => setHomepageSettings({
                          ...homepageSettings,
                          contact: {
                            ...homepageSettings.contact,
                            socialMedia: { ...homepageSettings.contact?.socialMedia, instagram: e.target.value }
                          }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">YouTube URL</label>
                      <input
                        type="text"
                        value={homepageSettings.contact?.socialMedia?.youtube || ''}
                        onChange={(e) => setHomepageSettings({
                          ...homepageSettings,
                          contact: {
                            ...homepageSettings.contact,
                            socialMedia: { ...homepageSettings.contact?.socialMedia, youtube: e.target.value }
                          }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Section Headings */}
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-semibold mb-4">📑 Section Headings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">News Heading</label>
                    <input
                      type="text"
                      value={homepageSettings.sections?.newsHeading || ''}
                      onChange={(e) => setHomepageSettings({
                        ...homepageSettings,
                        sections: { ...homepageSettings.sections, newsHeading: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Activities Heading</label>
                    <input
                      type="text"
                      value={homepageSettings.sections?.activitiesHeading || ''}
                      onChange={(e) => setHomepageSettings({
                        ...homepageSettings,
                        sections: { ...homepageSettings.sections, activitiesHeading: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Membership Heading</label>
                    <input
                      type="text"
                      value={homepageSettings.sections?.membershipHeading || ''}
                      onChange={(e) => setHomepageSettings({
                        ...homepageSettings,
                        sections: { ...homepageSettings.sections, membershipHeading: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hub Heading</label>
                    <input
                      type="text"
                      value={homepageSettings.sections?.hubHeading || ''}
                      onChange={(e) => setHomepageSettings({
                        ...homepageSettings,
                        sections: { ...homepageSettings.sections, hubHeading: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
              </div>

              {/* Footer Settings */}
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-semibold mb-4">🦶 Footer Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Footer Text</label>
                    <textarea
                      value={homepageSettings.footer?.text || ''}
                      onChange={(e) => setHomepageSettings({
                        ...homepageSettings,
                        footer: { ...homepageSettings.footer, text: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={homepageSettings.footer?.showSocialMedia || false}
                        onChange={(e) => setHomepageSettings({
                          ...homepageSettings,
                          footer: { ...homepageSettings.footer, showSocialMedia: e.target.checked }
                        })}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Show Social Media Icons in Footer
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end gap-2">
                <button
                  onClick={handleResetHomepage}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition"
                >
                  Reset to Defaults
                </button>
                <button
                  onClick={handleSaveHomepageSettings}
                  className="bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition"
                >
                  Save All Changes
                </button>
              </div>
            </div>
          )}

          {/* Activities Settings Tab */}
          {activeTab === 'activities-settings' && !pageSettings && (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading activities settings...</p>
            </div>
          )}

          {activeTab === 'activities-settings' && pageSettings && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-4">🏃 Activities Page Settings</h2>
                <p className="text-gray-600 mb-6">Customize the Activities page content, hero section, and activity visibility.</p>
                
                {/* Action Buttons */}
                <div className="flex gap-4 mb-6">
                  <button
                    onClick={handleSaveActivitiesSettings}
                    className="bg-primary text-white px-6 py-2 rounded hover:bg-primary/90 transition"
                  >
                    💾 Save All Changes
                  </button>
                  <button
                    onClick={handleResetActivities}
                    className="bg-red-500 text-white px-6 py-2 rounded hover:bg-red-600 transition"
                  >
                    🔄 Reset to Defaults
                  </button>
                </div>
              </div>

              {/* Hero Section */}
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-semibold mb-4">🎯 Hero Section</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                    <input
                      type="text"
                      value={pageSettings.activitiesPage?.hero?.title || ''}
                      onChange={(e) => setPageSettings({
                        ...pageSettings,
                        activitiesPage: {
                          ...pageSettings.activitiesPage,
                          hero: { ...pageSettings.activitiesPage.hero, title: e.target.value }
                        }
                      })}
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                    <textarea
                      value={pageSettings.activitiesPage?.hero?.subtitle || ''}
                      onChange={(e) => setPageSettings({
                        ...pageSettings,
                        activitiesPage: {
                          ...pageSettings.activitiesPage,
                          hero: { ...pageSettings.activitiesPage.hero, subtitle: e.target.value }
                        }
                      })}
                      rows={3}
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Primary Button Text</label>
                      <input
                        type="text"
                        value={pageSettings.activitiesPage?.hero?.buttonText || ''}
                        onChange={(e) => setPageSettings({
                          ...pageSettings,
                          activitiesPage: {
                            ...pageSettings.activitiesPage,
                            hero: { ...pageSettings.activitiesPage.hero, buttonText: e.target.value }
                          }
                        })}
                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Button Text</label>
                      <input
                        type="text"
                        value={pageSettings.activitiesPage?.hero?.secondaryButtonText || ''}
                        onChange={(e) => setPageSettings({
                          ...pageSettings,
                          activitiesPage: {
                            ...pageSettings.activitiesPage,
                            hero: { ...pageSettings.activitiesPage.hero, secondaryButtonText: e.target.value }
                          }
                        })}
                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hero Background Image</label>
                    <input
                      type="file"
                      onChange={handleActivitiesHeroUpload}
                      accept="image/*"
                      disabled={uploadingActivitiesHero}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                    {uploadingActivitiesHero && <p className="text-sm text-gray-500 mt-2">Uploading...</p>}
                    
                    {pageSettings.activitiesPage?.hero?.backgroundImage && (
                      <div className="mt-4">
                        <img 
                          src={`http://localhost:5000${pageSettings.activitiesPage.hero.backgroundImage}`}
                          alt="Hero background" 
                          className="max-w-md rounded-lg border"
                        />
                        <button
                          onClick={handleDeleteActivitiesHero}
                          className="mt-2 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                        >
                          Delete Image
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-semibold mb-4">📝 Page Description</h3>
                
                <textarea
                  value={pageSettings.activitiesPage?.description || ''}
                  onChange={(e) => setPageSettings({
                    ...pageSettings,
                    activitiesPage: {
                      ...pageSettings.activitiesPage,
                      description: e.target.value
                    }
                  })}
                  rows={4}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter page description..."
                />
              </div>

              {/* Show/Hide Activities */}
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-semibold mb-4">👁️ Activity Visibility</h3>
                <p className="text-sm text-gray-600 mb-4">Toggle which activities are visible on the page</p>
                
                <div className="space-y-3">
                  {[
                    { key: 'football', label: '⚽ Football' },
                    { key: 'walkAndTalk', label: '🚶 Walk & Talk' },
                    { key: 'womensCoffeeMornings', label: '☕ Women\'s Coffee Mornings' },
                    { key: 'womensAerobics', label: '🏃‍♀️ Women\'s Aerobics' },
                    { key: 'cycling', label: '🚴 Cycling' }
                  ].map(activity => (
                    <label key={activity.key} className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pageSettings.activitiesPage?.showActivities?.[activity.key] || false}
                        onChange={(e) => setPageSettings({
                          ...pageSettings,
                          activitiesPage: {
                            ...pageSettings.activitiesPage,
                            showActivities: {
                              ...pageSettings.activitiesPage.showActivities,
                              [activity.key]: e.target.checked
                            }
                          }
                        })}
                        className="w-5 h-5 text-primary"
                      />
                      <span className="text-gray-700 font-medium">{activity.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Bottom Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={handleSaveActivitiesSettings}
                  className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition"
                >
                  Save All Changes
                </button>
              </div>
            </div>
          )}

          {/* Community Hub Settings Tab */}
          {activeTab === 'hub-settings' && !pageSettings && (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading hub settings...</p>
            </div>
          )}

          {activeTab === 'hub-settings' && pageSettings && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-4">🏛️ Community Hub Page Settings</h2>
                <p className="text-gray-600 mb-6">Customize the Community Hub page content, features, and pricing.</p>
                
                {/* Action Buttons */}
                <div className="flex gap-4 mb-6">
                  <button
                    onClick={handleSaveHubSettings}
                    className="bg-primary text-white px-6 py-2 rounded hover:bg-primary/90 transition"
                  >
                    💾 Save All Changes
                  </button>
                  <button
                    onClick={handleResetHub}
                    className="bg-red-500 text-white px-6 py-2 rounded hover:bg-red-600 transition"
                  >
                    🔄 Reset to Defaults
                  </button>
                </div>
              </div>

              {/* Hero Section */}
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-semibold mb-4">🎯 Hero Section</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                    <input
                      type="text"
                      value={pageSettings.communityHubPage?.hero?.title || ''}
                      onChange={(e) => setPageSettings({
                        ...pageSettings,
                        communityHubPage: {
                          ...pageSettings.communityHubPage,
                          hero: { ...pageSettings.communityHubPage.hero, title: e.target.value }
                        }
                      })}
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                    <textarea
                      value={pageSettings.communityHubPage?.hero?.subtitle || ''}
                      onChange={(e) => setPageSettings({
                        ...pageSettings,
                        communityHubPage: {
                          ...pageSettings.communityHubPage,
                          hero: { ...pageSettings.communityHubPage.hero, subtitle: e.target.value }
                        }
                      })}
                      rows={3}
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Primary Button Text</label>
                      <input
                        type="text"
                        value={pageSettings.communityHubPage?.hero?.buttonText || ''}
                        onChange={(e) => setPageSettings({
                          ...pageSettings,
                          communityHubPage: {
                            ...pageSettings.communityHubPage,
                            hero: { ...pageSettings.communityHubPage.hero, buttonText: e.target.value }
                          }
                        })}
                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Button Text</label>
                      <input
                        type="text"
                        value={pageSettings.communityHubPage?.hero?.secondaryButtonText || ''}
                        onChange={(e) => setPageSettings({
                          ...pageSettings,
                          communityHubPage: {
                            ...pageSettings.communityHubPage,
                            hero: { ...pageSettings.communityHubPage.hero, secondaryButtonText: e.target.value }
                          }
                        })}
                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hero Background Image</label>
                    <input
                      type="file"
                      onChange={handleHubHeroUpload}
                      accept="image/*"
                      disabled={uploadingHubHero}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                    {uploadingHubHero && <p className="text-sm text-gray-500 mt-2">Uploading...</p>}
                    
                    {pageSettings.communityHubPage?.hero?.backgroundImage && (
                      <div className="mt-4">
                        <img 
                          src={`http://localhost:5000${pageSettings.communityHubPage.hero.backgroundImage}`}
                          alt="Hero background" 
                          className="max-w-md rounded-lg border"
                        />
                        <button
                          onClick={handleDeleteHubHero}
                          className="mt-2 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                        >
                          Delete Image
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-semibold mb-4">📝 Page Description</h3>
                
                <textarea
                  value={pageSettings.communityHubPage?.description || ''}
                  onChange={(e) => setPageSettings({
                    ...pageSettings,
                    communityHubPage: {
                      ...pageSettings.communityHubPage,
                      description: e.target.value
                    }
                  })}
                  rows={4}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter venue description..."
                />
              </div>

              {/* Pricing Settings */}
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-semibold mb-4">💰 Pricing Settings</h3>
                
                <div className="space-y-4">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={pageSettings.communityHubPage?.pricing?.showPricing || false}
                      onChange={(e) => setPageSettings({
                        ...pageSettings,
                        communityHubPage: {
                          ...pageSettings.communityHubPage,
                          pricing: {
                            ...pageSettings.communityHubPage.pricing,
                            showPricing: e.target.checked
                          }
                        }
                      })}
                      className="w-5 h-5 text-primary"
                    />
                    <span className="text-gray-700 font-medium">Show Pricing on Page</span>
                  </label>

                  {pageSettings.communityHubPage?.pricing?.showPricing && (
                    <div className="grid grid-cols-2 gap-4 pt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Deposit Amount</label>
                        <input
                          type="number"
                          value={pageSettings.communityHubPage?.pricing?.depositAmount || 0}
                          onChange={(e) => setPageSettings({
                            ...pageSettings,
                            communityHubPage: {
                              ...pageSettings.communityHubPage,
                              pricing: {
                                ...pageSettings.communityHubPage.pricing,
                                depositAmount: parseFloat(e.target.value)
                              }
                            }
                          })}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                        <select
                          value={pageSettings.communityHubPage?.pricing?.currency || 'GBP'}
                          onChange={(e) => setPageSettings({
                            ...pageSettings,
                            communityHubPage: {
                              ...pageSettings.communityHubPage,
                              pricing: {
                                ...pageSettings.communityHubPage.pricing,
                                currency: e.target.value
                              }
                            }
                          })}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="GBP">£ (GBP)</option>
                          <option value="USD">$ (USD)</option>
                          <option value="EUR">€ (EUR)</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Bottom Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={handleSaveHubSettings}
                  className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition"
                >
                  Save All Changes
                </button>
              </div>
            </div>
          )}

          {/* Page Editor Tab - Unified Page Management */}
        {activeTab === 'page-editor' && (
  <PageEditor />
)}

{false && (
            <div>
              <h2 className="text-2xl font-bold mb-6">📄 Page Editor</h2>
              <p className="text-gray-600 mb-6">
                Manage all website pages from one central location. Edit content, upload images, and customize each page.
              </p>

              {/* Sub-navigation for Page Editor */}
              <div className="mb-8 bg-gray-50 rounded-lg p-4">
                <div className="flex gap-4">
                  <button
                    onClick={() => setPageEditorSection('homepage')}
                    className={`px-6 py-3 rounded-lg font-semibold transition ${
                      pageEditorSection === 'homepage'
                        ? 'bg-primary text-white shadow-md'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    🏠 Homepage
                  </button>
                  <button
                    onClick={() => setPageEditorSection('activities')}
                    className={`px-6 py-3 rounded-lg font-semibold transition ${
                      pageEditorSection === 'activities'
                        ? 'bg-primary text-white shadow-md'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    🏃 Activities
                  </button>
                  <button
                    onClick={() => setPageEditorSection('hub')}
                    className={`px-6 py-3 rounded-lg font-semibold transition ${
                      pageEditorSection === 'hub'
                        ? 'bg-primary text-white shadow-md'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    🏛️ Community Hub
                  </button>
                </div>
              </div>

              {/* Homepage Section */}
              {pageEditorSection === 'homepage' && homepageSettings && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold">🏠 Homepage Settings</h2>
                    <div className="flex gap-2">
                      <button
                        onClick={handleResetHomepage}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition"
                      >
                        Reset to Defaults
                      </button>
                      <button
                        onClick={handleSaveHomepageSettings}
                        className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded transition"
                      >
                        Save All Changes
                      </button>
                    </div>
                  </div>

                  {/* Hero Section */}
                  <div className="bg-gray-50 p-6 rounded-lg mb-6">
                    <h3 className="text-xl font-semibold mb-4">🎯 Hero Section</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                        <input
                          type="text"
                          value={homepageSettings.hero?.title || ''}
                          onChange={(e) => setHomepageSettings({
                            ...homepageSettings,
                            hero: { ...homepageSettings.hero, title: e.target.value }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                        <textarea
                          value={homepageSettings.hero?.subtitle || ''}
                          onChange={(e) => setHomepageSettings({
                            ...homepageSettings,
                            hero: { ...homepageSettings.hero, subtitle: e.target.value }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          rows={3}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Primary Button Text</label>
                          <input
                            type="text"
                            value={homepageSettings.hero?.buttonText || ''}
                            onChange={(e) => setHomepageSettings({
                              ...homepageSettings,
                              hero: { ...homepageSettings.hero, buttonText: e.target.value }
                            })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Button Text</label>
                          <input
                            type="text"
                            value={homepageSettings.hero?.secondaryButtonText || ''}
                            onChange={(e) => setHomepageSettings({
                              ...homepageSettings,
                              hero: { ...homepageSettings.hero, secondaryButtonText: e.target.value }
                            })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hero Background Image</label>
                        {homepageSettings.hero?.backgroundImage && (
                          <div className="mb-2">
                            <img
                              src={`http://localhost:5000${homepageSettings.hero.backgroundImage}`}
                              alt="Hero Background"
                              className="h-32 w-full object-cover rounded-lg"
                            />
                            <button
                              onClick={handleDeleteHeroImage}
                              className="mt-2 bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm"
                            >
                              Delete Hero Image
                            </button>
                          </div>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleHeroImageUpload}
                          disabled={uploadingHero}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                        {uploadingHero && <p className="text-sm text-gray-500 mt-1">Uploading...</p>}
                      </div>
                    </div>
                  </div>

                  {/* Logo Section */}
                  <div className="bg-gray-50 p-6 rounded-lg mb-6">
                    <h3 className="text-xl font-semibold mb-4">📷 Logo</h3>
                    {homepageSettings.logo && (
                      <div className="mb-4">
                        <img
                          src={`http://localhost:5000${homepageSettings.logo}`}
                          alt="Logo"
                          className="h-24 object-contain bg-white p-2 rounded-lg"
                        />
                        <button
                          onClick={handleDeleteLogo}
                          className="mt-2 bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm"
                        >
                          Delete Logo
                        </button>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={uploadingLogo}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    {uploadingLogo && <p className="text-sm text-gray-500 mt-1">Uploading...</p>}
                  </div>

                  {/* Stats Section */}
                  <div className="bg-gray-50 p-6 rounded-lg mb-6">
                    <h3 className="text-xl font-semibold mb-4">📊 Statistics</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Members</label>
                        <input
                          type="text"
                          value={homepageSettings.stats?.members || ''}
                          onChange={(e) => setHomepageSettings({
                            ...homepageSettings,
                            stats: { ...homepageSettings.stats, members: e.target.value }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Activities</label>
                        <input
                          type="text"
                          value={homepageSettings.stats?.activities || ''}
                          onChange={(e) => setHomepageSettings({
                            ...homepageSettings,
                            stats: { ...homepageSettings.stats, activities: e.target.value }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Year Established</label>
                        <input
                          type="text"
                          value={homepageSettings.stats?.yearEstablished || ''}
                          onChange={(e) => setHomepageSettings({
                            ...homepageSettings,
                            stats: { ...homepageSettings.stats, yearEstablished: e.target.value }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hub Capacity</label>
                        <input
                          type="text"
                          value={homepageSettings.stats?.hubCapacity || ''}
                          onChange={(e) => setHomepageSettings({
                            ...homepageSettings,
                            stats: { ...homepageSettings.stats, hubCapacity: e.target.value }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Theme Colors */}
                  <div className="bg-gray-50 p-6 rounded-lg mb-6">
                    <h3 className="text-xl font-semibold mb-4">🎨 Theme Colors</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                      {[
                        { key: 'primaryColor', label: 'Primary Color' },
                        { key: 'secondaryColor', label: 'Secondary Color' },
                        { key: 'accentColor', label: 'Accent Color' },
                        { key: 'backgroundColor', label: 'Background Color' },
                        { key: 'textColor', label: 'Text Color' }
                      ].map(({ key, label }) => (
                        <div key={key}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-12 h-12 rounded border border-gray-300 cursor-pointer"
                              style={{ backgroundColor: homepageSettings.theme?.[key] || '#ffffff' }}
                              onClick={() => setShowColorPicker(showColorPicker === key ? null : key)}
                            />
                            <input
                              type="text"
                              value={homepageSettings.theme?.[key] || ''}
                              onChange={(e) => handleColorChange(key, e.target.value)}
                              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                              placeholder="#000000"
                            />
                          </div>
                          {showColorPicker === key && (
                            <div className="absolute z-10 mt-2">
                              <div
                                className="fixed inset-0"
                                onClick={() => setShowColorPicker(null)}
                              />
                              <ChromePicker
                                color={homepageSettings.theme?.[key] || '#ffffff'}
                                onChange={(color) => handleColorChange(key, color.hex)}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Font Family</label>
                      <select
                        value={homepageSettings.theme?.fontFamily || ''}
                        onChange={(e) => setHomepageSettings({
                          ...homepageSettings,
                          theme: { ...homepageSettings.theme, fontFamily: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="Inter, system-ui, sans-serif">Inter (Default)</option>
                        <option value="Arial, sans-serif">Arial</option>
                        <option value="Helvetica, sans-serif">Helvetica</option>
                        <option value="Georgia, serif">Georgia</option>
                        <option value="'Times New Roman', serif">Times New Roman</option>
                        <option value="'Courier New', monospace">Courier New</option>
                        <option value="Verdana, sans-serif">Verdana</option>
                        <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                      </select>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="bg-gray-50 p-6 rounded-lg mb-6">
                    <h3 className="text-xl font-semibold mb-4">📞 Contact Information</h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                          <input
                            type="text"
                            value={homepageSettings.contact?.phone || ''}
                            onChange={(e) => setHomepageSettings({
                              ...homepageSettings,
                              contact: { ...homepageSettings.contact, phone: e.target.value }
                            })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                          <input
                            type="email"
                            value={homepageSettings.contact?.email || ''}
                            onChange={(e) => setHomepageSettings({
                              ...homepageSettings,
                              contact: { ...homepageSettings.contact, email: e.target.value }
                            })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                        <input
                          type="text"
                          value={homepageSettings.contact?.address || ''}
                          onChange={(e) => setHomepageSettings({
                            ...homepageSettings,
                            contact: { ...homepageSettings.contact, address: e.target.value }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Facebook URL</label>
                          <input
                            type="text"
                            value={homepageSettings.contact?.socialMedia?.facebook || ''}
                            onChange={(e) => setHomepageSettings({
                              ...homepageSettings,
                              contact: {
                                ...homepageSettings.contact,
                                socialMedia: { ...homepageSettings.contact?.socialMedia, facebook: e.target.value }
                              }
                            })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Twitter URL</label>
                          <input
                            type="text"
                            value={homepageSettings.contact?.socialMedia?.twitter || ''}
                            onChange={(e) => setHomepageSettings({
                              ...homepageSettings,
                              contact: {
                                ...homepageSettings.contact,
                                socialMedia: { ...homepageSettings.contact?.socialMedia, twitter: e.target.value }
                              }
                            })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Instagram URL</label>
                          <input
                            type="text"
                            value={homepageSettings.contact?.socialMedia?.instagram || ''}
                            onChange={(e) => setHomepageSettings({
                              ...homepageSettings,
                              contact: {
                                ...homepageSettings.contact,
                                socialMedia: { ...homepageSettings.contact?.socialMedia, instagram: e.target.value }
                              }
                            })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">YouTube URL</label>
                          <input
                            type="text"
                            value={homepageSettings.contact?.socialMedia?.youtube || ''}
                            onChange={(e) => setHomepageSettings({
                              ...homepageSettings,
                              contact: {
                                ...homepageSettings.contact,
                                socialMedia: { ...homepageSettings.contact?.socialMedia, youtube: e.target.value }
                              }
                            })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section Headings */}
                  <div className="bg-gray-50 p-6 rounded-lg mb-6">
                    <h3 className="text-xl font-semibold mb-4">📑 Section Headings</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">News Heading</label>
                        <input
                          type="text"
                          value={homepageSettings.sections?.newsHeading || ''}
                          onChange={(e) => setHomepageSettings({
                            ...homepageSettings,
                            sections: { ...homepageSettings.sections, newsHeading: e.target.value }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Activities Heading</label>
                        <input
                          type="text"
                          value={homepageSettings.sections?.activitiesHeading || ''}
                          onChange={(e) => setHomepageSettings({
                            ...homepageSettings,
                            sections: { ...homepageSettings.sections, activitiesHeading: e.target.value }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Membership Heading</label>
                        <input
                          type="text"
                          value={homepageSettings.sections?.membershipHeading || ''}
                          onChange={(e) => setHomepageSettings({
                            ...homepageSettings,
                            sections: { ...homepageSettings.sections, membershipHeading: e.target.value }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hub Heading</label>
                        <input
                          type="text"
                          value={homepageSettings.sections?.hubHeading || ''}
                          onChange={(e) => setHomepageSettings({
                            ...homepageSettings,
                            sections: { ...homepageSettings.sections, hubHeading: e.target.value }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Footer Settings */}
                  <div className="bg-gray-50 p-6 rounded-lg mb-6">
                    <h3 className="text-xl font-semibold mb-4">🦶 Footer Settings</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Footer Text</label>
                        <textarea
                          value={homepageSettings.footer?.text || ''}
                          onChange={(e) => setHomepageSettings({
                            ...homepageSettings,
                            footer: { ...homepageSettings.footer, text: e.target.value }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          rows={3}
                        />
                      </div>
                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={homepageSettings.footer?.showSocialMedia || false}
                            onChange={(e) => setHomepageSettings({
                              ...homepageSettings,
                              footer: { ...homepageSettings.footer, showSocialMedia: e.target.checked }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm font-medium text-gray-700">
                            Show Social Media Icons in Footer
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={handleResetHomepage}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition"
                    >
                      Reset to Defaults
                    </button>
                    <button
                      onClick={handleSaveHomepageSettings}
                      className="bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition"
                    >
                      Save All Changes
                    </button>
                  </div>
                </div>
              )}

              {/* Activities Section */}
              {pageEditorSection === 'activities' && pageSettings && (
                <div className="space-y-6">
                  <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
                    <p className="text-green-800">
                      <strong>Activities Settings:</strong> Edit the activities overview page and customize individual activity pages.
                    </p>
                  </div>

                  {/* Activities Overview or Individual Activity Selector */}
                  <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
                    <h3 className="text-xl font-bold mb-4">Select What to Edit</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => setSelectedActivity('overview')}
                        className={`p-4 rounded-lg border-2 transition ${
                          selectedActivity === 'overview'
                            ? 'border-primary bg-primary/10'
                            : 'border-gray-300 hover:border-primary/50'
                        }`}
                      >
                        <div className="text-3xl mb-2">📋</div>
                        <div className="font-semibold">Activities Overview Page</div>
                        <div className="text-sm text-gray-600 mt-1">Main activities landing page</div>
                      </button>
                      
                      <div className="p-4 rounded-lg border-2 border-gray-300">
                        <div className="text-3xl mb-2">⚽</div>
                        <div className="font-semibold mb-2">Individual Activities</div>
                        <select
                          value={selectedActivity === 'overview' ? '' : selectedActivity}
                          onChange={(e) => setSelectedActivity(e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg"
                        >
                          <option value="">Choose activity...</option>
                          <option value="football">⚽ Football</option>
                          <option value="walkAndTalk">🚶 Walk & Talk</option>
                          <option value="womensCoffeeMornings">☕ Women's Coffee Mornings</option>
                          <option value="womensAerobics">🏃‍♀️ Women's Aerobics</option>
                          <option value="cycling">🚴 Cycling</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Show appropriate editor based on selection */}
                  {selectedActivity === 'overview' && (
                    <div>
                      <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold">📋 Activities Overview Page</h2>
                        <div className="flex gap-2">
                          <button
                            onClick={handleResetActivities}
                            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition"
                          >
                            Reset to Defaults
                          </button>
                          <button
                            onClick={handleSaveActivitiesSettings}
                            className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded transition"
                          >
                            Save All Changes
                          </button>
                        </div>
                      </div>

                      {/* Hero Section */}
                      <div className="bg-gray-50 p-6 rounded-lg mb-6">
                        <h3 className="text-xl font-semibold mb-4">🎯 Hero Section</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                            <input
                              type="text"
                              value={pageSettings.activitiesPage?.hero?.title || ''}
                              onChange={(e) => setPageSettings({
                                ...pageSettings,
                                activitiesPage: {
                                  ...pageSettings.activitiesPage,
                                  hero: { ...pageSettings.activitiesPage.hero, title: e.target.value }
                                }
                              })}
                              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                            <textarea
                              value={pageSettings.activitiesPage?.hero?.subtitle || ''}
                              onChange={(e) => setPageSettings({
                                ...pageSettings,
                                activitiesPage: {
                                  ...pageSettings.activitiesPage,
                                  hero: { ...pageSettings.activitiesPage.hero, subtitle: e.target.value }
                                }
                              })}
                              rows={3}
                              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Primary Button Text</label>
                              <input
                                type="text"
                                value={pageSettings.activitiesPage?.hero?.buttonText || ''}
                                onChange={(e) => setPageSettings({
                                  ...pageSettings,
                                  activitiesPage: {
                                    ...pageSettings.activitiesPage,
                                    hero: { ...pageSettings.activitiesPage.hero, buttonText: e.target.value }
                                  }
                                })}
                                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Button Text</label>
                              <input
                                type="text"
                                value={pageSettings.activitiesPage?.hero?.secondaryButtonText || ''}
                                onChange={(e) => setPageSettings({
                                  ...pageSettings,
                                  activitiesPage: {
                                    ...pageSettings.activitiesPage,
                                    hero: { ...pageSettings.activitiesPage.hero, secondaryButtonText: e.target.value }
                                  }
                                })}
                                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Hero Background Image</label>
                            <input
                              type="file"
                              onChange={handleActivitiesHeroUpload}
                              accept="image/*"
                              disabled={uploadingActivitiesHero}
                              className="w-full px-4 py-2 border rounded-lg"
                            />
                            {uploadingActivitiesHero && <p className="text-sm text-gray-500 mt-2">Uploading...</p>}
                            
                            {pageSettings.activitiesPage?.hero?.backgroundImage && (
                              <div className="mt-4">
                                <img 
                                  src={`http://localhost:5000${pageSettings.activitiesPage.hero.backgroundImage}`}
                                  alt="Hero background" 
                                  className="max-w-md rounded-lg border"
                                />
                                <button
                                  onClick={handleDeleteActivitiesHero}
                                  className="mt-2 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                                >
                                  Delete Image
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="bg-gray-50 p-6 rounded-lg mb-6">
                        <h3 className="text-xl font-semibold mb-4">📝 Page Description</h3>
                        <textarea
                          value={pageSettings.activitiesPage?.description || ''}
                          onChange={(e) => setPageSettings({
                            ...pageSettings,
                            activitiesPage: {
                              ...pageSettings.activitiesPage,
                              description: e.target.value
                            }
                          })}
                          rows={4}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Enter activities page description..."
                        />
                      </div>

                      {/* Multiple Images Gallery */}
                      <div className="bg-gray-50 p-6 rounded-lg mb-6">
                        <h3 className="text-xl font-semibold mb-4">📸 Image Gallery</h3>
                        <p className="text-sm text-gray-600 mb-4">Upload up to 10 images to display on the activities page</p>
                        
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={async (e) => {
                            const files = e.target.files;
                            if (!files || files.length === 0) return;
                            
                            if (files.length > 10) {
                              alert('Maximum 10 images allowed at once');
                              return;
                            }
                            
                            setUploadingImages(true);
                            const formData = new FormData();
                            Array.from(files).forEach(file => {
                              formData.append('images', file);
                            });
                            
                            try {
                              const response = await axios.post(
                                `${API_URL}/pages/upload-images/activities`,
                                formData,
                                {
                                  headers: {
                                    'Content-Type': 'multipart/form-data',
                                    'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                                  }
                                }
                              );
                              
                              if (response.data.success) {
                                // Refresh settings
                                const settingsRes = await axios.get(`${API_URL}/pages`);
                                setPageSettings(settingsRes.data.data);
                                alert('Images uploaded successfully!');
                              }
                            } catch (error) {
                              console.error('Error uploading images:', error);
                              alert('Failed to upload images');
                            } finally {
                              setUploadingImages(false);
                              e.target.value = '';
                            }
                          }}
                          disabled={uploadingImages}
                          className="w-full px-4 py-2 border rounded-lg"
                        />
                        {uploadingImages && <p className="text-sm text-gray-500 mt-2">Uploading images...</p>}
                        
                        {/* Display existing images */}
                        {pageSettings.activitiesPage?.images && pageSettings.activitiesPage.images.length > 0 && (
                          <div className="mt-6">
                            <h4 className="font-semibold mb-3">Uploaded Images ({pageSettings.activitiesPage.images.length})</h4>
                            <div className="grid grid-cols-3 gap-4">
                              {pageSettings.activitiesPage.images.map((img: string, idx: number) => (
                                <div key={idx} className="relative group">
                                  <img 
                                    src={`http://localhost:5000${img}`}
                                    alt={`Gallery ${idx + 1}`}
                                    className="w-full h-48 object-cover rounded-lg border"
                                  />
                                  <button
                                    onClick={async () => {
                                      if (!window.confirm('Delete this image?')) return;
                                      
                                      try {
                                        const response = await axios.delete(
                                          `${API_URL}/pages/delete-image/activities`,
                                          {
                                            headers: {
                                              'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                                            },
                                            data: { imagePath: img }
                                          }
                                        );
                                        
                                        if (response.data.success) {
                                          // Refresh settings
                                          const settingsRes = await axios.get(`${API_URL}/pages`);
                                          setPageSettings(settingsRes.data.data);
                                          alert('Image deleted successfully!');
                                        }
                                      } catch (error) {
                                        console.error('Error deleting image:', error);
                                        alert('Failed to delete image');
                                      }
                                    }}
                                    className="absolute top-2 right-2 bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 opacity-0 group-hover:opacity-100 transition"
                                  >
                                    Delete
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Activity Visibility Toggles */}
                      <div className="bg-gray-50 p-6 rounded-lg mb-6">
                        <h3 className="text-xl font-semibold mb-4">👁️ Activity Visibility</h3>
                        <p className="text-sm text-gray-600 mb-4">Control which activities appear on the Activities page</p>
                        
                        <div className="space-y-3">
                          {[
                            { key: 'football', label: 'Football', icon: '⚽' },
                            { key: 'walkAndTalk', label: 'Walk & Talk', icon: '🚶' },
                            { key: 'womensCoffeeMornings', label: "Women's Coffee Mornings", icon: '☕' },
                            { key: 'womensAerobics', label: "Women's Aerobics", icon: '🏃‍♀️' },
                            { key: 'cycling', label: 'Cycling', icon: '🚴' }
                          ].map(({ key, label, icon }) => (
                            <label key={key} className="flex items-center space-x-3 cursor-pointer p-3 bg-white rounded-lg border hover:border-primary transition">
                              <input
                                type="checkbox"
                                checked={pageSettings.activitiesPage?.showActivities?.[key] !== false}
                                onChange={(e) => setPageSettings({
                                  ...pageSettings,
                                  activitiesPage: {
                                    ...pageSettings.activitiesPage,
                                    showActivities: {
                                      ...pageSettings.activitiesPage?.showActivities,
                                      [key]: e.target.checked
                                    }
                                  }
                                })}
                                className="w-5 h-5 text-primary"
                              />
                              <span className="text-2xl">{icon}</span>
                              <span className="text-gray-700 font-medium">{label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Bottom Save Button */}
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={handleResetActivities}
                          className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition"
                        >
                          Reset to Defaults
                        </button>
                        <button
                          onClick={handleSaveActivitiesSettings}
                          className="bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition"
                        >
                          Save All Changes
                        </button>
                      </div>
                    </div>
                  )}

                  {selectedActivity && selectedActivity !== 'overview' && pageSettings.activityPages?.[selectedActivity] && (
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <h3 className="text-xl font-bold mb-4">
                        Edit {pageSettings.activityPages[selectedActivity].title || selectedActivity}
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                          <input
                            type="text"
                            value={pageSettings.activityPages[selectedActivity].title || ''}
                            onChange={(e) => {
                              const updated = {...pageSettings};
                              updated.activityPages[selectedActivity].title = e.target.value;
                              setPageSettings(updated);
                            }}
                            className="w-full px-4 py-2 border rounded-lg"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                          <textarea
                            value={pageSettings.activityPages[selectedActivity].description || ''}
                            onChange={(e) => {
                              const updated = {...pageSettings};
                              updated.activityPages[selectedActivity].description = e.target.value;
                              setPageSettings(updated);
                            }}
                            rows={4}
                            className="w-full px-4 py-2 border rounded-lg"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Day</label>
                            <input
                              type="text"
                              value={pageSettings.activityPages[selectedActivity].schedule?.day || ''}
                              onChange={(e) => {
                                const updated = {...pageSettings};
                                updated.activityPages[selectedActivity].schedule = {
                                  ...updated.activityPages[selectedActivity].schedule,
                                  day: e.target.value
                                };
                                setPageSettings(updated);
                              }}
                              className="w-full px-4 py-2 border rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                            <input
                              type="text"
                              value={pageSettings.activityPages[selectedActivity].schedule?.time || ''}
                              onChange={(e) => {
                                const updated = {...pageSettings};
                                updated.activityPages[selectedActivity].schedule = {
                                  ...updated.activityPages[selectedActivity].schedule,
                                  time: e.target.value
                                };
                                setPageSettings(updated);
                              }}
                              className="w-full px-4 py-2 border rounded-lg"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                          <input
                            type="text"
                            value={pageSettings.activityPages[selectedActivity].location || ''}
                            onChange={(e) => {
                              const updated = {...pageSettings};
                              updated.activityPages[selectedActivity].location = e.target.value;
                              setPageSettings(updated);
                            }}
                            className="w-full px-4 py-2 border rounded-lg"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Details</label>
                          <textarea
                            value={pageSettings.activityPages[selectedActivity].details || ''}
                            onChange={(e) => {
                              const updated = {...pageSettings};
                              updated.activityPages[selectedActivity].details = e.target.value;
                              setPageSettings(updated);
                            }}
                            rows={4}
                            className="w-full px-4 py-2 border rounded-lg"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email</label>
                            <input
                              type="email"
                              value={pageSettings.activityPages[selectedActivity].contactEmail || ''}
                              onChange={(e) => {
                                const updated = {...pageSettings};
                                updated.activityPages[selectedActivity].contactEmail = e.target.value;
                                setPageSettings(updated);
                              }}
                              className="w-full px-4 py-2 border rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Contact Phone</label>
                            <input
                              type="tel"
                              value={pageSettings.activityPages[selectedActivity].contactPhone || ''}
                              onChange={(e) => {
                                const updated = {...pageSettings};
                                updated.activityPages[selectedActivity].contactPhone = e.target.value;
                                setPageSettings(updated);
                              }}
                              className="w-full px-4 py-2 border rounded-lg"
                            />
                          </div>
                        </div>

                        {/* Multiple Images Upload */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Activity Images</label>
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={async (e) => {
                              if (!e.target.files || e.target.files.length === 0) return;
                              
                              const token = localStorage.getItem('adminToken');
                              const formData = new FormData();
                              Array.from(e.target.files).forEach(file => {
                                formData.append('images', file);
                              });
                              
                              setUploadingImages(true);
                              try {
                                await axios.post(`${API_URL}/pages/upload-images/${selectedActivity}`, formData, {
                                  headers: {
                                    Authorization: `Bearer ${token}`,
                                    'Content-Type': 'multipart/form-data'
                                  }
                                });
                                alert('Images uploaded successfully!');
                                fetchData();
                              } catch (error: any) {
                                alert(error.response?.data?.message || 'Error uploading images');
                              } finally {
                                setUploadingImages(false);
                                e.target.value = '';
                              }
                            }}
                            disabled={uploadingImages}
                            className="w-full px-4 py-2 border rounded-lg"
                          />
                          {uploadingImages && <p className="text-sm text-gray-500 mt-2">Uploading...</p>}
                          
                          {/* Display uploaded images */}
                          {pageSettings.activityPages[selectedActivity].images && pageSettings.activityPages[selectedActivity].images.length > 0 && (
                            <div className="mt-4 grid grid-cols-3 gap-4">
                              {pageSettings.activityPages[selectedActivity].images.map((img: string, idx: number) => (
                                <div key={idx} className="relative">
                                  <img 
                                    src={`http://localhost:5000${img}`}
                                    alt={`Activity ${idx + 1}`}
                                    className="w-full h-32 object-cover rounded-lg border"
                                  />
                                  <button
                                    onClick={async () => {
                                      if (!window.confirm('Delete this image?')) return;
                                      const token = localStorage.getItem('adminToken');
                                      try {
                                        await axios.delete(`${API_URL}/pages/delete-image/${selectedActivity}`, {
                                          headers: { Authorization: `Bearer ${token}` },
                                          data: { imagePath: img }
                                        });
                                        alert('Image deleted!');
                                        fetchData();
                                      } catch (error: any) {
                                        alert(error.response?.data?.message || 'Error deleting image');
                                      }
                                    }}
                                    className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                                  >
                                    Delete
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={pageSettings.activityPages[selectedActivity].isActive || false}
                            onChange={(e) => {
                              const updated = {...pageSettings};
                              updated.activityPages[selectedActivity].isActive = e.target.checked;
                              setPageSettings(updated);
                            }}
                            className="w-5 h-5"
                          />
                          <label className="text-sm font-medium text-gray-700">Activity is Active</label>
                        </div>

                        <div className="flex justify-end gap-4 pt-4">
                          <button
                            onClick={async () => {
                              const token = localStorage.getItem('adminToken');
                              if (!token) {
                                alert('Not authenticated');
                                return;
                              }
                              
                              try {
                                await axios.put(
                                  `${API_URL}/pages/activity/${selectedActivity}`,
                                  pageSettings.activityPages[selectedActivity],
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                alert('Activity updated successfully!');
                                fetchData();
                              } catch (error: any) {
                                alert(error.response?.data?.message || 'Error updating activity');
                              }
                            }}
                            className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition"
                          >
                            💾 Save Activity Changes
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Community Hub Section */}
              {pageEditorSection === 'hub' && pageSettings && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold mb-4">🏛️ Community Hub Page Settings</h2>
                    <p className="text-gray-600 mb-6">Customize the Community Hub page content, features, and pricing.</p>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-4 mb-6">
                      <button
                        onClick={handleSaveHubSettings}
                        className="bg-primary text-white px-6 py-2 rounded hover:bg-primary/90 transition"
                      >
                        💾 Save All Changes
                      </button>
                      <button
                        onClick={handleResetHub}
                        className="bg-red-500 text-white px-6 py-2 rounded hover:bg-red-600 transition"
                      >
                        🔄 Reset to Defaults
                      </button>
                    </div>
                  </div>

                  {/* Hero Section */}
                  <div className="bg-gray-50 p-6 rounded-lg mb-6">
                    <h3 className="text-xl font-semibold mb-4">🎯 Hero Section</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                        <input
                          type="text"
                          value={pageSettings.communityHubPage?.hero?.title || ''}
                          onChange={(e) => setPageSettings({
                            ...pageSettings,
                            communityHubPage: {
                              ...pageSettings.communityHubPage,
                              hero: { ...pageSettings.communityHubPage.hero, title: e.target.value }
                            }
                          })}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                        <textarea
                          value={pageSettings.communityHubPage?.hero?.subtitle || ''}
                          onChange={(e) => setPageSettings({
                            ...pageSettings,
                            communityHubPage: {
                              ...pageSettings.communityHubPage,
                              hero: { ...pageSettings.communityHubPage.hero, subtitle: e.target.value }
                            }
                          })}
                          rows={3}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Primary Button Text</label>
                          <input
                            type="text"
                            value={pageSettings.communityHubPage?.hero?.buttonText || ''}
                            onChange={(e) => setPageSettings({
                              ...pageSettings,
                              communityHubPage: {
                                ...pageSettings.communityHubPage,
                                hero: { ...pageSettings.communityHubPage.hero, buttonText: e.target.value }
                              }
                            })}
                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Button Text</label>
                          <input
                            type="text"
                            value={pageSettings.communityHubPage?.hero?.secondaryButtonText || ''}
                            onChange={(e) => setPageSettings({
                              ...pageSettings,
                              communityHubPage: {
                                ...pageSettings.communityHubPage,
                                hero: { ...pageSettings.communityHubPage.hero, secondaryButtonText: e.target.value }
                              }
                            })}
                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hero Background Image</label>
                        <input
                          type="file"
                          onChange={handleHubHeroUpload}
                          accept="image/*"
                          disabled={uploadingHubHero}
                          className="w-full px-4 py-2 border rounded-lg"
                        />
                        {uploadingHubHero && <p className="text-sm text-gray-500 mt-2">Uploading...</p>}
                        
                        {pageSettings.communityHubPage?.hero?.backgroundImage && (
                          <div className="mt-4">
                            <img 
                              src={`http://localhost:5000${pageSettings.communityHubPage.hero.backgroundImage}`}
                              alt="Hero background" 
                              className="max-w-md rounded-lg border"
                            />
                            <button
                              onClick={handleDeleteHubHero}
                              className="mt-2 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                            >
                              Delete Image
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="bg-gray-50 p-6 rounded-lg mb-6">
                    <h3 className="text-xl font-semibold mb-4">📝 Page Description</h3>
                    
                    <textarea
                      value={pageSettings.communityHubPage?.description || ''}
                      onChange={(e) => setPageSettings({
                        ...pageSettings,
                        communityHubPage: {
                          ...pageSettings.communityHubPage,
                          description: e.target.value
                        }
                      })}
                      rows={4}
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Enter venue description..."
                    />
                  </div>

                  {/* Pricing Settings */}
                  <div className="bg-gray-50 p-6 rounded-lg mb-6">
                    <h3 className="text-xl font-semibold mb-4">💰 Pricing Settings</h3>
                    
                    <div className="space-y-4">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={pageSettings.communityHubPage?.pricing?.showPricing || false}
                          onChange={(e) => setPageSettings({
                            ...pageSettings,
                            communityHubPage: {
                              ...pageSettings.communityHubPage,
                              pricing: {
                                ...pageSettings.communityHubPage.pricing,
                                showPricing: e.target.checked
                              }
                            }
                          })}
                          className="w-5 h-5 text-primary"
                        />
                        <span className="text-gray-700 font-medium">Show Pricing on Page</span>
                      </label>

                      {pageSettings.communityHubPage?.pricing?.showPricing && (
                        <div className="grid grid-cols-2 gap-4 pt-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Deposit Amount</label>
                            <input
                              type="number"
                              value={pageSettings.communityHubPage?.pricing?.depositAmount || 0}
                              onChange={(e) => setPageSettings({
                                ...pageSettings,
                                communityHubPage: {
                                  ...pageSettings.communityHubPage,
                                  pricing: {
                                    ...pageSettings.communityHubPage.pricing,
                                    depositAmount: parseFloat(e.target.value)
                                  }
                                }
                              })}
                              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                            <select
                              value={pageSettings.communityHubPage?.pricing?.currency || 'GBP'}
                              onChange={(e) => setPageSettings({
                                ...pageSettings,
                                communityHubPage: {
                                  ...pageSettings.communityHubPage,
                                  pricing: {
                                    ...pageSettings.communityHubPage.pricing,
                                    currency: e.target.value
                                  }
                                }
                              })}
                              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                            >
                              <option value="GBP">£ (GBP)</option>
                              <option value="USD">$ (USD)</option>
                              <option value="EUR">€ (EUR)</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Multiple Images Gallery */}
                  <div className="bg-gray-50 p-6 rounded-lg mb-6">
                    <h3 className="text-xl font-semibold mb-4">📸 Hub Image Gallery</h3>
                    <p className="text-sm text-gray-600 mb-4">Upload up to 10 images to showcase the community hub</p>
                    
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={async (e) => {
                        const files = e.target.files;
                        if (!files || files.length === 0) return;
                        
                        if (files.length > 10) {
                          alert('Maximum 10 images allowed at once');
                          return;
                        }
                        
                        setUploadingImages(true);
                        const formData = new FormData();
                        Array.from(files).forEach(file => {
                          formData.append('images', file);
                        });
                        
                        try {
                          const response = await axios.post(
                            `${API_URL}/pages/upload-images/community-hub`,
                            formData,
                            {
                              headers: {
                                'Content-Type': 'multipart/form-data',
                                'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                              }
                            }
                          );
                          
                          if (response.data.success) {
                            // Refresh settings
                            const settingsRes = await axios.get(`${API_URL}/pages`);
                            setPageSettings(settingsRes.data.data);
                            alert('Images uploaded successfully!');
                          }
                        } catch (error) {
                          console.error('Error uploading images:', error);
                          alert('Failed to upload images');
                        } finally {
                          setUploadingImages(false);
                          e.target.value = '';
                        }
                      }}
                      disabled={uploadingImages}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                    {uploadingImages && <p className="text-sm text-gray-500 mt-2">Uploading images...</p>}
                    
                    {/* Display existing images */}
                    {pageSettings.communityHubPage?.images && pageSettings.communityHubPage.images.length > 0 && (
                      <div className="mt-6">
                        <h4 className="font-semibold mb-3">Uploaded Images ({pageSettings.communityHubPage.images.length})</h4>
                        <div className="grid grid-cols-3 gap-4">
                          {pageSettings.communityHubPage.images.map((img: string, idx: number) => (
                            <div key={idx} className="relative group">
                              <img 
                                src={`http://localhost:5000${img}`}
                                alt={`Hub Gallery ${idx + 1}`}
                                className="w-full h-48 object-cover rounded-lg border"
                              />
                              <button
                                onClick={async () => {
                                  if (!window.confirm('Delete this image?')) return;
                                  
                                  try {
                                    const response = await axios.delete(
                                      `${API_URL}/pages/delete-image/community-hub`,
                                      {
                                        headers: {
                                          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                                        },
                                        data: { imagePath: img }
                                      }
                                    );
                                    
                                    if (response.data.success) {
                                      // Refresh settings
                                      const settingsRes = await axios.get(`${API_URL}/pages`);
                                      setPageSettings(settingsRes.data.data);
                                      alert('Image deleted successfully!');
                                    }
                                  } catch (error) {
                                    console.error('Error deleting image:', error);
                                    alert('Failed to delete image');
                                  }
                                }}
                                className="absolute top-2 right-2 bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 opacity-0 group-hover:opacity-100 transition"
                              >
                                Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Bottom Save Button */}
                  <div className="flex justify-end">
                    <button
                      onClick={handleSaveHubSettings}
                      className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition"
                    >
                      Save All Changes
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// BookingsManagement Component
const BookingsManagement: React.FC<{ bookings: any[]; onRefresh: () => void }> = ({ bookings, onRefresh }) => {
  const [expandedBooking, setExpandedBooking] = useState<string | null>(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showBookedModal, setShowBookedModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [editFormData, setEditFormData] = useState<any>({});
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  // Calculate booking price automatically
  const calculateBookingPrice = (startTime: string, endTime: string): { hours: number, bookingFee: number, cleaningFee: number, deposit: number, totalPrice: number, netRevenue: number } => {
    // Parse times (format: "HH:MM")
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    // Calculate total hours
    const startTotalMins = startHour * 60 + startMin;
    const endTotalMins = endHour * 60 + endMin;
    const totalMins = endTotalMins - startTotalMins;
    const hours = totalMins / 60;
    
    // Pricing: £50 per hour, £30 cleaning fee, £200 deposit
    const bookingFee = hours * 50;
    const cleaningFee = 30;
    const deposit = 200;
    const totalPrice = bookingFee + cleaningFee + deposit;
    const netRevenue = bookingFee; // Only booking fee counts as revenue
    
    return { hours, bookingFee, cleaningFee, deposit, totalPrice, netRevenue };
  };

  // Format date to UK format (DD/MM/YYYY)
  const formatDateUK = (dateString: string): string => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const updateBookingStatus = async (bookingId: string, status: string) => {
    if (status === 'booked') {
      const booking = bookings.find(b => b._id === bookingId);
      setSelectedBooking(booking);
      setShowBookedModal(true);
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(
        `${API_URL}/hub/bookings/${bookingId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`Booking marked as ${status}`);
      onRefresh();
    } catch (error) {
      console.error('Error updating booking:', error);
      alert('Failed to update booking status');
    }
  };

  const handleBookedSubmit = async () => {
    if (!selectedBooking) {
      alert('No booking selected');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      
      // Calculate pricing automatically
      const pricing = calculateBookingPrice(selectedBooking.startTime, selectedBooking.endTime);
      
      await axios.patch(
        `${API_URL}/hub/bookings/${selectedBooking._id}/status`,
        {
          status: 'booked',
          bookedDate: selectedBooking.eventDate, // Use the event date from inquiry
          totalAmount: pricing.totalPrice, // Full amount including cleaning and deposit
          bookingFee: pricing.bookingFee, // Only this goes to revenue
          cleaningFee: pricing.cleaningFee,
          deposit: pricing.deposit,
          hours: pricing.hours
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Booking confirmed successfully!');
      setShowBookedModal(false);
      setSelectedBooking(null);
      onRefresh();
    } catch (error) {
      console.error('Error confirming booking:', error);
      alert('Failed to confirm booking');
    }
  };

  const handleEditBooking = (booking: any) => {
    setSelectedBooking(booking);
    setEditFormData({
      firstName: booking.firstName,
      lastName: booking.lastName,
      email: booking.email,
      phone: booking.phone,
      address: booking.address,
      postCode: booking.postCode,
      eventType: booking.eventType,
      eventDate: booking.eventDate,
      startTime: booking.startTime,
      endTime: booking.endTime,
      numberOfGuests: booking.numberOfGuests,
      foodAndDrinks: booking.foodAndDrinks,
      hireType: booking.hireType,
      charityNumber: booking.charityNumber || '',
      additionalRequirements: booking.additionalRequirements || '',
      bookedDate: booking.bookedDate ? new Date(booking.bookedDate).toISOString().split('T')[0] : '',
      totalAmount: booking.totalAmount || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API_URL}/hub/bookings/${selectedBooking._id}`,
        editFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Booking updated successfully!');
      setShowEditModal(false);
      setEditFormData({});
      onRefresh();
    } catch (error) {
      console.error('Error updating booking:', error);
      alert('Failed to update booking');
    }
  };

  const handleDeleteBooking = async (bookingId: string, bookingName: string) => {
    if (!window.confirm(`Are you sure you want to permanently delete the booking for ${bookingName}? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(
        `${API_URL}/hub/bookings/${bookingId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Booking deleted successfully!');
      onRefresh();
    } catch (error) {
      console.error('Error deleting booking:', error);
      alert('Failed to delete booking');
    }
  };

  const filteredBookings = bookings.filter(booking => {
    if (selectedFilter === 'all') return true;
    return booking.status === selectedFilter;
  });

  const bookedInMonth = bookings.filter(b => 
    b.status === 'booked' && 
    b.bookedDate && 
    b.bookedDate.startsWith(selectedMonth)
  );

  const monthlyRevenue = bookedInMonth.reduce((sum, b) => {
    const amount = (b.totalAmount || 0) - (b.depositRefunded ? 0 : b.depositAmount || 200);
    return sum + amount;
  }, 0);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'replied': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'not-interested': return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'booked': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-4">🏢 Hub Bookings Management</h2>
        
        {/* Revenue Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <p className="text-sm text-yellow-700">Pending Enquiries</p>
            <p className="text-2xl font-bold text-yellow-800">
              {bookings.filter(b => b.status === 'pending').length}
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <p className="text-sm text-blue-700">Replied</p>
            <p className="text-2xl font-bold text-blue-800">
              {bookings.filter(b => b.status === 'replied').length}
            </p>
          </div>
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <p className="text-sm text-green-700">Booked</p>
            <p className="text-2xl font-bold text-green-800">
              {bookings.filter(b => b.status === 'booked').length}
            </p>
          </div>
          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <p className="text-sm text-purple-700">Monthly Revenue</p>
            <div className="flex items-center gap-2">
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="text-xs border rounded px-2 py-1"
              />
            </div>
            <p className="text-2xl font-bold text-purple-800">
              £{monthlyRevenue.toFixed(2)}
            </p>
            <p className="text-xs text-purple-600">{bookedInMonth.length} bookings</p>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setSelectedFilter('all')}
            className={`px-4 py-2 rounded ${selectedFilter === 'all' ? 'bg-primary text-white' : 'bg-gray-200'}`}
          >
            All ({bookings.length})
          </button>
          <button
            onClick={() => setSelectedFilter('pending')}
            className={`px-4 py-2 rounded ${selectedFilter === 'pending' ? 'bg-yellow-500 text-white' : 'bg-yellow-100'}`}
          >
            Pending ({bookings.filter(b => b.status === 'pending').length})
          </button>
          <button
            onClick={() => setSelectedFilter('replied')}
            className={`px-4 py-2 rounded ${selectedFilter === 'replied' ? 'bg-blue-500 text-white' : 'bg-blue-100'}`}
          >
            Replied ({bookings.filter(b => b.status === 'replied').length})
          </button>
          <button
            onClick={() => setSelectedFilter('booked')}
            className={`px-4 py-2 rounded ${selectedFilter === 'booked' ? 'bg-green-500 text-white' : 'bg-green-100'}`}
          >
            Booked ({bookings.filter(b => b.status === 'booked').length})
          </button>
          <button
            onClick={() => setSelectedFilter('not-interested')}
            className={`px-4 py-2 rounded ${selectedFilter === 'not-interested' ? 'bg-gray-500 text-white' : 'bg-gray-100'}`}
          >
            Not Interested ({bookings.filter(b => b.status === 'not-interested').length})
          </button>
        </div>
      </div>

      {/* Bookings List */}
      <div className="space-y-3">
        {filteredBookings.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No bookings found</p>
        ) : (
          filteredBookings.map((booking) => (
            <div
              key={booking._id}
              className={`border-2 rounded-lg overflow-hidden ${getStatusColor(booking.status)}`}
            >
              {/* Collapsed Header */}
              <div
                className="p-4 cursor-pointer hover:bg-opacity-50"
                onClick={() => setExpandedBooking(expandedBooking === booking._id ? null : booking._id)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-bold text-lg">
                        {booking.firstName} {booking.lastName}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(booking.status)}`}>
                        {booking.status.toUpperCase()}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-sm">
                      <div>
                        <span className="text-gray-600">📅 Event Date:</span>
                        <p className="font-semibold">{booking.eventDate}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">📧 Enquiry Date:</span>
                        <p className="font-semibold">
                          {new Date(booking.enquiryDate || booking.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600">🎉 Event Type:</span>
                        <p className="font-semibold">{booking.eventType}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">👥 Guests:</span>
                        <p className="font-semibold">{booking.numberOfGuests}</p>
                      </div>
                    </div>
                  </div>
                  <button className="text-2xl">
                    {expandedBooking === booking._id ? '▲' : '▼'}
                  </button>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedBooking === booking._id && (
                <div className="border-t-2 p-4 bg-white">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Contact Information */}
                    <div>
                      <h4 className="font-bold mb-2 text-primary">📞 Contact Information</h4>
                      <div className="space-y-1 text-sm">
                        <p><strong>Email:</strong> {booking.email}</p>
                        <p><strong>Phone:</strong> {booking.phone}</p>
                        <p><strong>Address:</strong> {booking.address}</p>
                        <p><strong>Post Code:</strong> {booking.postCode}</p>
                      </div>
                    </div>

                    {/* Event Details */}
                    <div>
                      <h4 className="font-bold mb-2 text-primary">🎊 Event Details</h4>
                      <div className="space-y-1 text-sm">
                        <p><strong>Event Type:</strong> {booking.eventType}</p>
                        <p><strong>Date:</strong> {booking.eventDate}</p>
                        <p><strong>Time:</strong> {booking.startTime} - {booking.endTime}</p>
                        <p><strong>Number of Guests:</strong> {booking.numberOfGuests}</p>
                        <p><strong>Food & Drinks:</strong> {booking.foodAndDrinks === 'yes' ? 'Yes ✓' : 'No'}</p>
                        <p><strong>Hire Type:</strong> {booking.hireType === 'private' ? 'Private Hire' : 'Non-Profit Hire'}</p>
                        {booking.charityNumber && <p><strong>Charity Number:</strong> {booking.charityNumber}</p>}
                      </div>
                    </div>
                  </div>

                  {/* Additional Requirements */}
                  {booking.additionalRequirements && (
                    <div className="mb-4">
                      <h4 className="font-bold mb-2 text-primary">📝 Additional Requirements</h4>
                      <p className="text-sm bg-gray-50 p-3 rounded">{booking.additionalRequirements}</p>
                    </div>
                  )}

                  {/* Booked Details (if status is booked) */}
                  {booking.status === 'booked' && (
                    <div className="mb-4 bg-green-50 border border-green-200 p-3 rounded">
                      <h4 className="font-bold mb-2 text-green-800">✅ Booking Confirmed</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <p><strong>Booked Date:</strong> {new Date(booking.bookedDate).toLocaleDateString()}</p>
                        <p><strong>Total Amount:</strong> £{booking.totalAmount?.toFixed(2) || '0.00'}</p>
                        <p><strong>Deposit:</strong> £{booking.depositAmount || 200} {booking.depositRefunded ? '(Refunded)' : '(To be refunded)'}</p>
                        <p><strong>Net Revenue:</strong> £{((booking.totalAmount || 0) - (booking.depositRefunded ? 0 : booking.depositAmount || 200)).toFixed(2)}</p>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 flex-wrap">
                    {booking.status === 'pending' && (
                      <>
                        <button
                          onClick={() => updateBookingStatus(booking._id, 'replied')}
                          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                          ✉️ Mark as Replied
                        </button>
                        <button
                          onClick={() => updateBookingStatus(booking._id, 'booked')}
                          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        >
                          ✅ Mark as Booked
                        </button>
                        <button
                          onClick={() => updateBookingStatus(booking._id, 'not-interested')}
                          className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        >
                          ❌ Not Interested
                        </button>
                      </>
                    )}
                    {booking.status === 'replied' && (
                      <div className="w-full">
                        <div className="bg-blue-50 border border-blue-200 p-3 rounded mb-3">
                          <p className="text-sm text-blue-800 mb-2">
                            ℹ️ <strong>Next Step:</strong> Once customer confirms, you can move this booking to "Booked" status to finalize the reservation.
                          </p>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => updateBookingStatus(booking._id, 'booked')}
                            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                          >
                            ✅ Confirm Booking
                          </button>
                          <button
                            onClick={() => updateBookingStatus(booking._id, 'not-interested')}
                            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                          >
                            ❌ Not Interested
                          </button>
                          <button
                            onClick={() => updateBookingStatus(booking._id, 'pending')}
                            className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                          >
                            ⏪ Back to Pending
                          </button>
                        </div>
                      </div>
                    )}
                    {booking.status === 'booked' && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditBooking(booking)}
                          className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
                        >
                          ✏️ Edit Booking
                        </button>
                        <button
                          onClick={() => handleDeleteBooking(booking._id, `${booking.firstName} ${booking.lastName}`)}
                          className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    )}
                    {booking.status === 'not-interested' && (
                      <button
                        onClick={() => updateBookingStatus(booking._id, 'pending')}
                        className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                      >
                        ⏪ Back to Pending
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Booked Modal */}
      {showBookedModal && selectedBooking && (() => {
        const pricing = calculateBookingPrice(selectedBooking.startTime, selectedBooking.endTime);
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg max-w-lg w-full mx-4">
              <h3 className="text-xl font-bold mb-4">✅ Confirm Booking</h3>
              <p className="mb-4 text-gray-600">
                Review booking details for{' '}
                <strong>{selectedBooking.firstName} {selectedBooking.lastName}</strong>
              </p>
              
              <div className="space-y-4">
                {/* Booking Information - Read Only */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-3">📋 Booking Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Event Date:</span>
                      <span className="font-semibold">{formatDateUK(selectedBooking.eventDate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Event Time:</span>
                      <span className="font-semibold">{selectedBooking.startTime} - {selectedBooking.endTime}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Duration:</span>
                      <span className="font-semibold">{pricing.hours} hour{pricing.hours !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Event Type:</span>
                      <span className="font-semibold">{selectedBooking.eventType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Number of Guests:</span>
                      <span className="font-semibold">{selectedBooking.numberOfGuests}</span>
                    </div>
                  </div>
                </div>

                {/* Price Breakdown */}
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-900 mb-3">💰 Price Breakdown</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Booking Fee ({pricing.hours}h × £50/h):</span>
                      <span className="font-semibold">£{pricing.bookingFee.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cleaning Fee:</span>
                      <span className="font-semibold">£{pricing.cleaningFee.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Refundable Deposit:</span>
                      <span className="font-semibold">£{pricing.deposit.toFixed(2)}</span>
                    </div>
                    <hr className="my-2 border-green-300" />
                    <div className="flex justify-between">
                      <span className="font-bold text-green-900">Total Amount Due:</span>
                      <span className="font-bold text-green-900 text-lg">£{pricing.totalPrice.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Revenue Information */}
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <h4 className="font-semibold text-yellow-900 mb-2">📊 Revenue Information</h4>
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-700">
                      ✓ Booking Fee <strong>(£{pricing.bookingFee.toFixed(2)})</strong> will be added to monthly revenue
                    </p>
                    <p className="text-gray-700">
                      ✗ Cleaning Fee <strong>(£{pricing.cleaningFee.toFixed(2)})</strong> - not counted in revenue
                    </p>
                    <p className="text-gray-700">
                      ✗ Deposit <strong>(£{pricing.deposit.toFixed(2)})</strong> - refundable, not revenue
                    </p>
                    <div className="mt-2 pt-2 border-t border-yellow-300">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-yellow-900">Net Revenue:</span>
                        <span className="font-bold text-yellow-900 text-lg">£{pricing.netRevenue.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  onClick={handleBookedSubmit}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 font-semibold"
                >
                  ✓ Confirm Booking
                </button>
                <button
                  onClick={() => {
                    setShowBookedModal(false);
                    setSelectedBooking(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Edit Booking Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white p-6 rounded-lg max-w-3xl w-full mx-4 my-8">
            <h3 className="text-xl font-bold mb-4">Edit Booking Details</h3>
            <p className="mb-4 text-gray-600">
              Editing booking for <strong>{selectedBooking?.firstName} {selectedBooking?.lastName}</strong>
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto mb-4 pr-2">
              {/* Contact Information */}
              <div className="space-y-3">
                <h4 className="font-semibold text-primary">Contact Information</h4>
                <div>
                  <label className="block text-xs font-medium mb-1">First Name *</label>
                  <input
                    type="text"
                    value={editFormData.firstName}
                    onChange={(e) => setEditFormData({ ...editFormData, firstName: e.target.value })}
                    className="w-full px-2 py-1 text-sm border rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Last Name *</label>
                  <input
                    type="text"
                    value={editFormData.lastName}
                    onChange={(e) => setEditFormData({ ...editFormData, lastName: e.target.value })}
                    className="w-full px-2 py-1 text-sm border rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Email *</label>
                  <input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    className="w-full px-2 py-1 text-sm border rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Phone *</label>
                  <input
                    type="text"
                    value={editFormData.phone}
                    onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                    className="w-full px-2 py-1 text-sm border rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Address *</label>
                  <input
                    type="text"
                    value={editFormData.address}
                    onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                    className="w-full px-2 py-1 text-sm border rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Post Code *</label>
                  <input
                    type="text"
                    value={editFormData.postCode}
                    onChange={(e) => setEditFormData({ ...editFormData, postCode: e.target.value })}
                    className="w-full px-2 py-1 text-sm border rounded"
                  />
                </div>
              </div>

              {/* Event Details */}
              <div className="space-y-3">
                <h4 className="font-semibold text-primary">Event Details</h4>
                <div>
                  <label className="block text-xs font-medium mb-1">Event Type *</label>
                  <input
                    type="text"
                    value={editFormData.eventType}
                    onChange={(e) => setEditFormData({ ...editFormData, eventType: e.target.value })}
                    className="w-full px-2 py-1 text-sm border rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Event Date *</label>
                  <input
                    type="date"
                    value={editFormData.eventDate}
                    onChange={(e) => setEditFormData({ ...editFormData, eventDate: e.target.value })}
                    className="w-full px-2 py-1 text-sm border rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Start Time *</label>
                  <input
                    type="time"
                    value={editFormData.startTime}
                    onChange={(e) => setEditFormData({ ...editFormData, startTime: e.target.value })}
                    className="w-full px-2 py-1 text-sm border rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">End Time *</label>
                  <input
                    type="time"
                    value={editFormData.endTime}
                    onChange={(e) => setEditFormData({ ...editFormData, endTime: e.target.value })}
                    className="w-full px-2 py-1 text-sm border rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Number of Guests *</label>
                  <input
                    type="number"
                    value={editFormData.numberOfGuests}
                    onChange={(e) => setEditFormData({ ...editFormData, numberOfGuests: e.target.value })}
                    className="w-full px-2 py-1 text-sm border rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Food & Drinks</label>
                  <select
                    value={editFormData.foodAndDrinks}
                    onChange={(e) => setEditFormData({ ...editFormData, foodAndDrinks: e.target.value })}
                    className="w-full px-2 py-1 text-sm border rounded"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Hire Type *</label>
                  <select
                    value={editFormData.hireType}
                    onChange={(e) => setEditFormData({ ...editFormData, hireType: e.target.value })}
                    className="w-full px-2 py-1 text-sm border rounded"
                  >
                    <option value="private">Private Hire</option>
                    <option value="non-profit">Non-Profit Hire</option>
                  </select>
                </div>
                {editFormData.hireType === 'non-profit' && (
                  <div>
                    <label className="block text-xs font-medium mb-1">Charity Number</label>
                    <input
                      type="text"
                      value={editFormData.charityNumber}
                      onChange={(e) => setEditFormData({ ...editFormData, charityNumber: e.target.value })}
                      className="w-full px-2 py-1 text-sm border rounded"
                    />
                  </div>
                )}
              </div>

              {/* Booking Confirmation (for booked status) */}
              {selectedBooking?.status === 'booked' && (
                <div className="md:col-span-2 space-y-3">
                  <h4 className="font-semibold text-primary">Booking Confirmation</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Booked Date</label>
                      <input
                        type="date"
                        value={editFormData.bookedDate}
                        onChange={(e) => setEditFormData({ ...editFormData, bookedDate: e.target.value })}
                        className="w-full px-2 py-1 text-sm border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Total Amount (£)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={editFormData.totalAmount}
                        onChange={(e) => setEditFormData({ ...editFormData, totalAmount: e.target.value })}
                        className="w-full px-2 py-1 text-sm border rounded"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Requirements */}
              <div className="md:col-span-2">
                <label className="block text-xs font-medium mb-1">Additional Requirements</label>
                <textarea
                  value={editFormData.additionalRequirements}
                  onChange={(e) => setEditFormData({ ...editFormData, additionalRequirements: e.target.value })}
                  className="w-full px-2 py-1 text-sm border rounded"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={handleEditSubmit}
                className="flex-1 px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
              >
                Save Changes
              </button>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditFormData({});
                }}
                className="flex-1 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
